import {
    DashboardFilterParameter,
    DashboardViewFilterSectionFilters,
    DashboardWidgetConfiguration,
    DashboardWidgetOrderByField,
    DashboardWidgetSizing,
    DashboardWidgetType,
    DashboardWidgetVariantInfo,
    WidgetForcedFilterRequirement,
} from "./types";
import {
    BarChartConfig,
    BarChartDataPoint,
    LineChartConfig,
    LineChartDataPoint,
    TreemapWidgetConfig
} from './components/content/charts';
import {darken, lighten} from "@mui/system/colorManipulator";
import {TableConfig, TableDataPoint} from "./components/content/table";
import ProductInformationPopover
    from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import React from "react";
import {Box, Checkbox, Divider, Theme, Typography} from "@mui/material";
import {Property} from "csstype";
import {SystemCssProperties} from "@mui/system/styleFunctionSx/styleFunctionSx";
import {FontSpec} from "chart.js";
// @ts-ignore
import {ReactComponent as CheckboxIcon} from "../../../../../../assets/images/check-box/checkbox.svg";
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../assets/images/check-box/checkbox-active.svg";
import {TreemapScriptableContext} from "chartjs-chart-treemap";
import {Context} from "chartjs-plugin-datalabels";
import DateUtils, {DateUtilsTypes} from "../../../../../../core/services/date-utils";
import {DataGridDensities} from "../../../../../../packages/data-grid";
import useQuery from "../../../../../hooks/use-query";
import {DateRangeUtils} from "../../../../../components/app-specific/date-range-controller";
import {InfoBlockConfig} from "./components/content/info-block";
import {DEFAULT_NUMBER_VISIBILITY_MODE, NumberVisibilityMode, NumberVisibilityModes} from "./hooks/use-number-visibility-mode";

export type GetMinHeightReturnValue = SystemCssProperties<Theme>['minHeight'];

/** Dense error Alert (~40px) plus an 8px gap; keep fixed so wrapping copy cannot change layout. */
export const FETCH_ERROR_BANNER_HEIGHT = 48;

export type WidgetOriginalParameter = {
    fieldName: string;
    type: string;
    title: string;
}

/**
 * The enum for all possible dashboard widget types
 */
export class DashboardWidgetIdentifiers {
    static readonly sales_todaySales = "6d6e4416-bbc3-4bf9-9220-d917ee280e2c";
    static readonly sales_wtdSales = "97dd18c7-fb1d-40b9-b53a-db8262ad5bf3";
    static readonly sales_mtdSales = "8df4471e-c1d4-4f47-9bd2-751508033d15";
    static readonly sales_ytdSales = "cf5159c1-7309-41f7-b4c1-376e1e41e899";
    static readonly sales_annualSales = "92e72f23-e302-49b6-8af6-50d0603da0ba";
    static readonly sales_monthlySales = "ca527f65-d2ed-4383-8676-33ee81102953";
    static readonly sales_ytdAnnualSales = "9862ae4e-3855-4816-8f53-ad7e8609f257";
    static readonly sales_ytdParts = '965586f4-e00e-43a5-b2e4-17d216a3d507';
    static readonly sales_ytdCustomer = "92bab7cd-78be-4a51-af9d-3bb26af81214";
    static readonly sales_salesByCurrency = "9feba9bf-cb25-4b82-9ead-13cd53961450";
    static readonly sales_customerTreemap = "396857e7-40e3-4511-9446-bdf4d473c48f";
    static readonly sales_salesByTerritory = "f86d31d6-c7fd-477e-801a-76daa23ac920";
    static readonly sales_salesByWarehouse = "8a1cadaf-71f7-40df-bbc5-9ce3328b6ce5";
    static readonly sales_lostSalesParts = "8d312267-02f7-421a-9e22-21c38b91e5bf";
    static readonly sales_lostSalesCustomer = "36023382-1b21-484e-b460-4beda9ffc5ec";
    // TODO: Updaite with real GUIDs when available
    static readonly sales_salesByProvince = "00000000-0000-0000-0000-000000000001";
    static readonly sales_salesByCity = "00000000-0000-0000-0000-000000000002";
    static readonly sales_salesByAccountManager = "00000000-0000-0000-0000-000000000003";
    static readonly sales_lostSalesByProvince = "00000000-0000-0000-0000-000000000000-1";
    static readonly sales_lostSalesByCity = "00000000-0000-0000-0000-000000000000-2";

    static readonly users_customerLogins = "8eae8811-faad-43f5-87cc-06855fe4cfa0";

    static readonly warranty_requestsByStatus = "8c2d5da7-5e68-4816-a015-0c0b11b4b015";
    static readonly warranty_requestsByVendorStatus = "ed365b93-5421-4ab3-8e39-34aa33b96d6b";
    static readonly warranty_requestsAnnually = "2e996e5b-cc62-4fd8-af6e-5e0dc3106edc";
    static readonly warranty_requestsByCustomer = "08a6ce67-6129-4338-8010-787fb320ce3e";
    static readonly warranty_requestsByMonth = "f3dccc0e-4519-4b8a-8b6a-97538df37cc1";
    static readonly warranty_requestsByWarehouse = "0e2de755-f8ad-4471-8144-9d1998aee290";
    static readonly warranty_requestsByPart = "af1356b7-1484-46c9-a0cf-a454cc5aa0e4";
    static readonly warranty_requestsByOperator = "707efef7-4e6a-4ea6-a8b3-b4296642d29d";
    static readonly warranty_requestsByPartCategory = "2cef8350-2102-4865-b5b9-e90cbd9b6286";
    static readonly warranty_requestsByReason = "bd1b2332-f8e5-4c88-9f63-eae1c3eb5d15";
    static readonly warranty_requestsByVendor = "1d59e4c5-ec32-44d7-9da0-c83327d7651d";
    static readonly warranty_requestsByPartAndSales = "92de165e-f9fc-4cee-bd5d-0123b76db80a";
    static readonly warranty_requestsByCategoryAndSales = "263a06c3-b3b7-4cbc-8ccb-a13eba5fdc8e";
    static readonly warranty_warrantyAndReturnPopup = "4ea0641f-c737-42bd-bd76-6f98529c223e";
    static readonly warranty_warrantyRatioStats = "b8eeef86-abf6-4eb3-9eac-3c0dde684252";

    // AR Transactions
    static readonly ar_transactionsByCustomer = '508219b8-135f-427f-96c5-e646d82c8c33';
    static readonly ar_transactionsBalance = '958addfb-0c2d-4e84-aafb-717e58d1ee34';

    // Purchase — TODO: Update remaining mock GUIDs when available
    // Unused backend GUID (Vendor Monthly Purchase): 66119bc2-fe64-47af-838d-3efd5f5012eb
    static readonly purchase_todayPurchases = "10000000-0000-0000-0000-000000000001"; // TODO: real GUID
    static readonly purchase_wtdPurchases = "de5159a9-fa22-40d0-a59e-29e3fe1ca214";
    static readonly purchase_mtdPurchases = "3af7bd6b-1737-484b-b92c-18a1f49d8a09";
    static readonly purchase_ytdPurchases = "f28bcfb5-3e8d-43ba-8b2a-b9d73ea3f32c";
    static readonly purchase_annualPurchases = "a909360f-6f78-456d-a077-8858ce057302";
    static readonly purchase_monthlyPurchases = "2d32a182-2a04-4616-86fb-d00ada67ca0b";
    static readonly purchase_ytdAnnualPurchases = "4f53a9f5-3296-48a6-88e0-25ecbe41a7a7";
    static readonly purchase_ytdParts = "ca83caf1-85c6-4bb0-a81a-79fbe123232c";
    static readonly purchase_ytdVendor = "51fa72ac-3c2e-45dd-b22a-9439ebb74eb3";
    static readonly purchase_purchaseByCurrency = "10000000-0000-0000-0000-00000000000a"; // TODO: real GUID
    static readonly purchase_vendorTreemap = "1fe6529e-60dc-41a0-8e28-0673c447fa41";
    static readonly purchase_purchaseByTerritory = "10000000-0000-0000-0000-00000000000c"; // TODO: real GUID
    static readonly purchase_purchaseByWarehouse = "10000000-0000-0000-0000-00000000000d"; // TODO: real GUID
}

/**
 * The variants for each dashboard widget type
 */
export class DashboardWidgetVariants {
    // parts
    static readonly salesYtdPartsBarChartSalesBased = "Sales";
    static readonly salesYtdPartsBarChartQuantityBased = "Quantity";
    static readonly salesYtdPartsBarChartAvgMarginBased = "Avg Margin";
    static readonly salesYtdPartsBarChartGrossProfitBased = "Gross Profit";
    // customer
    static readonly salesYtdCustomerBarChartSalesBased = "Sales";
    static readonly salesYtdCustomerBarChartQuantityBased = "Quantity";
    static readonly salesYtdCustomerBarChartAvgMarginBased = "Avg Margin";
    static readonly salesYtdCustomerBarChartGrossProfitBased = "Gross Profit";
    // customer treemap
    static readonly salesCustomerTreemapSalesBased = "Sales";
    static readonly salesCustomerTreemapQuantityBased = "Quantity";
    static readonly salesCustomerTreemapAvgMarginBased = "Avg Margin";
    static readonly salesCustomerTreemapGrossProfitBased = "Gross Profit";
    // lost - parts
    static readonly salesLostSalesPartsBarChartSalesBased = "Sales";
    static readonly salesLostSalesPartsBarChartQuantityBased = "Quantity";
    // lost - customer
    static readonly salesLostSalesCustomerBarChartSalesBased = "Sales";
    static readonly salesLostSalesCustomerBarChartQuantityBased = "Quantity";
    // province
    static readonly salesSalesByProvinceBarChartSalesBased = "Sales";
    static readonly salesSalesByProvinceBarChartQuantityBased = "Quantity";
    static readonly salesSalesByProvinceBarChartAvgMarginBased = "Avg Margin";
    static readonly salesSalesByProvinceBarChartGrossProfitBased = "Gross Profit";
    // city
    static readonly salesSalesByCityBarChartSalesBased = "Sales";
    static readonly salesSalesByCityBarChartQuantityBased = "Quantity";
    static readonly salesSalesByCityBarChartAvgMarginBased = "Avg Margin";
    static readonly salesSalesByCityBarChartGrossProfitBased = "Gross Profit";
    // account manager
    static readonly salesSalesByAccountManagerBarChartSalesBased = "Sales";
    static readonly salesSalesByAccountManagerBarChartQuantityBased = "Quantity";
    static readonly salesSalesByAccountManagerBarChartAvgMarginBased = "Avg Margin";
    static readonly salesSalesByAccountManagerBarChartGrossProfitBased = "Gross Profit";
    // territory
    static readonly salesSalesByTerritoryBarChartSalesBased = "Sales";
    static readonly salesSalesByTerritoryBarChartQuantityBased = "Quantity";
    static readonly salesSalesByTerritoryBarChartAvgMarginBased = "Avg Margin";
    static readonly salesSalesByTerritoryBarChartGrossProfitBased = "Gross Profit";
    // warehouse
    static readonly salesSalesByWarehouseBarChartSalesBased = "Sales";
    static readonly salesSalesByWarehouseBarChartQuantityBased = "Quantity";
    static readonly salesSalesByWarehouseBarChartAvgMarginBased = "Avg Margin";
    static readonly salesSalesByWarehouseBarChartGrossProfitBased = "Gross Profit";
    // lost - province
    static readonly salesLostSalesByProvinceBarChartSalesBased = "Sales";
    static readonly salesLostSalesByProvinceBarChartQuantityBased = "Quantity";
    // lost - city
    static readonly salesLostSalesByCityBarChartSalesBased = "Sales";
    static readonly salesLostSalesByCityBarChartQuantityBased = "Quantity";
    // purchase - parts
    static readonly purchaseYtdPartsBarChartCostBased = "Cost";
    static readonly purchaseYtdPartsBarChartQuantityBased = "Quantity";
    // purchase - vendor
    static readonly purchaseYtdVendorBarChartCostBased = "Cost";
    static readonly purchaseYtdVendorBarChartQuantityBased = "Quantity";
    // purchase - vendor treemap
    static readonly purchaseVendorTreemapCostBased = "Cost";
    static readonly purchaseVendorTreemapQuantityBased = "Quantity";
    // purchase - territory
    static readonly purchasePurchaseByTerritoryBarChartCostBased = "Cost";
    static readonly purchasePurchaseByTerritoryBarChartQuantityBased = "Quantity";
    // purchase - warehouse
    static readonly purchasePurchaseByWarehouseBarChartCostBased = "Cost";
    static readonly purchasePurchaseByWarehouseBarChartQuantityBased = "Quantity";
}

/**
 * The configuration for a dashboard widget.
 */
export class DashboardWidgetConfig {

    private static readonly pageSize = 10;

    private static readonly widgetSizingConfig: Record<DashboardWidgetType | 'default', DashboardWidgetSizing> = {
        [DashboardWidgetType.table]: {
            size: {xs: 12, md: 6, lg: 6, xl: 4},
            minSize: {xs: 6, md: 6, lg: 4, xl: 3},
        },
        [DashboardWidgetType.lineChart]: {
            size: {xs: 12, md: 6, lg: 6, xl: 4},
            minSize: {xs: 6, md: 6, lg: 4, xl: 3},
        },
        [DashboardWidgetType.barChart]: {
            size: {xs: 12, md: 6, lg: 6, xl: 4},
            minSize: {xs: 6, md: 6, lg: 4, xl: 3},
        },
        [DashboardWidgetType.infoBlock]: {
            size: {xs: 6, md: 4, lg: 3, xl: 2},
            minSize: {xs: 4, md: 6, lg: 2, xl: 2},
        },
        [DashboardWidgetType.treeMapOld]: {
            size: {xs: 12, md: 6, lg: 6, xl: 4},
            minSize: {xs: 6, md: 6, lg: 4, xl: 3},
        },
        [DashboardWidgetType.treeMap]: {
            size: {xs: 12, md: 6, lg: 6, xl: 4},
            minSize: {xs: 6, md: 6, lg: 4, xl: 3},
        },
        default: {
            size: {xs: 12, md: 6, lg: 6, xl: 4},
            minSize: {xs: 6, md: 6, lg: 4, xl: 3},
        }
    }

    private static readonly widgetParameters = {
        [DashboardWidgetIdentifiers.sales_todaySales]: {
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_wtdSales]: {
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_mtdSales]: {
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_ytdSales]: {
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_annualSales]: {
            year: {fieldName: "Year", type: "int", title: "Year"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_monthlySales]: {
            monthNumber: {fieldName: "MonthNumber", type: "string", title: "Month No"},
            year: {fieldName: "Year", type: "int", title: "Year"},
            month: {fieldName: "Month", type: "string", title: "Month"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_ytdAnnualSales]: {
            year: {fieldName: "Year", type: "int", title: "Year"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_ytdParts]: {
            partNo: {fieldName: "PartNo", type: "string", title: "Part No"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_ytdCustomer]: {
            customerId: {fieldName: "Customer_Id", type: "int", title: "Customer ID"},
            customerName: {fieldName: "Customer_Name", type: "string", title: "Customer Name"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_salesByCurrency]: {
            currencyDisplay: {fieldName: "currencyDisplay", type: "string", title: "Currency Display"},
            currencyCode: {fieldName: "Currency_Code", type: "string", title: "Currency Code"},
            sales: {fieldName: "sales", type: "decimal", title: "Sales"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_salesByTerritory]: {
            territoryCode: {fieldName: "TerritoryCode", type: "string", title: "Territory Code"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_salesByWarehouse]: {
            warehouse: {fieldName: "Warehouse", type: "string", title: "Warehouse"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        },
        [DashboardWidgetIdentifiers.sales_customerTreemap]: {
            customerId: {fieldName: "Customer_Id", type: "int", title: "Customer ID"},
            customerName: {fieldName: "Customer_Name", type: "string", title: "Customer Name"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_lostSalesParts]: {
            partNo: {fieldName: "PartNo", type: "string", title: "Part No"},
            lostSales: {fieldName: "LostSales", type: "decimal", title: "Lost Sales"},
            lostQty: {fieldName: "LostQty", type: "decimal", title: "Lost Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_lostSalesCustomer]: {
            customerId: {fieldName: "Customer_Id", type: "int", title: "Customer ID"},
            customerName: {fieldName: "Customer_Name", type: "string", title: "Customer Name"},
            lostSales: {fieldName: "LostSales", type: "decimal", title: "Lost Sales"},
            lostQty: {fieldName: "LostQty", type: "decimal", title: "Lost Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_salesByProvince]: {
            provinceCode: {fieldName: "ProvinceCode", type: "string", title: "Province Code"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_salesByCity]: {
            city: {fieldName: "City", type: "string", title: "City"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_salesByAccountManager]: {
            accountManagerCode: {fieldName: "AccountManagerCode", type: "string", title: "Account Manager Code"},
            sales: {fieldName: "Sales", type: "decimal", title: "Sales"},
            avgMargin: {fieldName: "AvgMargin", type: "decimal", title: "Avg Margin"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
            grossProfit: {fieldName: "GrossProfit", type: "decimal", title: "Gross Profit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_lostSalesByProvince]: {
            provinceCode: {fieldName: "ProvinceCode", type: "string", title: "Province Code"},
            lostSales: {fieldName: "LostSales", type: "decimal", title: "Lost Sales"},
            lostQty: {fieldName: "LostQty", type: "decimal", title: "Lost Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.sales_lostSalesByCity]: {
            city: {fieldName: "City", type: "string", title: "City"},
            lostSales: {fieldName: "LostSales", type: "decimal", title: "Lost Sales"},
            lostQty: {fieldName: "LostQty", type: "decimal", title: "Lost Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.ar_transactionsByCustomer]: {
            customerId: {fieldName: "Customer_Id", type: "int", title: "Customer ID"},
            customerName: {fieldName: "Customer_Name", type: "string", title: "Customer Name"},
            totalBalance: {fieldName: "TotalBalance", type: "decimal", title: "Total Balance"},
            totalDebitAmount: {fieldName: "TotalDebitAmount", type: "decimal", title: "Total Debit"},
            totalCreditAmount: {fieldName: "TotalCreditAmount", type: "decimal", title: "Total Credit"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.ar_transactionsBalance]: {
            totalBalance: {fieldName: "TotalBalance", type: "decimal", title: "Total Balance"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.users_customerLogins]: {
            customerId: {fieldName: "Customer_Id", type: "int", title: "Customer ID"},
            customerName: {fieldName: "Customer_Name", type: "string", title: "Customer Name"},
            loginCount: {fieldName: "LoginCount", type: "int", title: "Login Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByStatus]: {
            status: {fieldName: "Status", type: "string", title: "Status"},
            statusId: {fieldName: "Status_Id", type: "int", title: "Status ID"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByVendorStatus]: {
            status: {fieldName: "Status", type: "string", title: "Vendor Status"},
            statusId: {fieldName: "Status_Id", type: "int", title: "Vendor Status ID"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsAnnually]: {
            year: {fieldName: "Year", type: "int", title: "Year"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByCustomer]: {
            customerName: {fieldName: "Customer_Name", type: "string", title: "Customer Name"},
            customerId: {fieldName: "Customer_Id", type: "int", title: "Customer ID"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByMonth]: {
            year: {fieldName: "Year", type: "int", title: "Year"},
            month: {fieldName: "Month", type: "string", title: "Month"},
            monthNumber: {fieldName: "MonthNumber", type: "int", title: "Month No"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByWarehouse]: {
            warehouse: {fieldName: "Warehouse", type: "string", title: "Warehouse"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByPart]: {
            partNo: {fieldName: "PartNo", type: "string", title: "Part No"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByOperator]: {
            operatorFirstname: {fieldName: "Operator_Firstname", type: "string", title: "Operator Firstname"},
            operatorLastname: {fieldName: "Operator_Lastname", type: "string", title: "Operator Lastname"},
            operatorId: {fieldName: "Operator_Id", type: "string", title: "Operator ID"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByPartCategory]: {
            category: {fieldName: "Category", type: "string", title: "Category"},
            categoryId: {fieldName: "Category_Id", type: "int", title: "Category ID"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByReason]: {
            reasonCode: {fieldName: "Reason_Code", type: "string", title: "Reason Code"},
            reasonTitle: {fieldName: "Reason_Title", type: "string", title: "Reason Title"},
            reasonId: {fieldName: "Reason_Id", type: "int", title: "Reason ID"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByVendor]: {
            vendorId: {fieldName: "Vendor_Id", type: "int", title: "Vendor ID"},
            vendorCode: {fieldName: "Vendor_Code", type: "string", title: "Vendor Code"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByPartAndSales]: {
            partNo: {fieldName: "PartNo", type: "string", title: "Part No"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
            totalSales: {fieldName: "TotalSales", type: "decimal", title: "Total Sales"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Sales Qty"},
            warrantyPct: {fieldName: "WarrantyPct", type: "decimal", title: "Warranty Pct"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales]: {
            category: {fieldName: "Category", type: "string", title: "Category"},
            categoryId: {fieldName: "Category_Id", type: "int", title: "Category ID"},
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
            totalSales: {fieldName: "TotalSales", type: "decimal", title: "Total Sales"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Sales Qty"},
            warrantyPct: {fieldName: "WarrantyPct", type: "decimal", title: "Warranty Pct"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_warrantyAndReturnPopup]: {
            year: {fieldName: "Year", type: "int", title: "Year"},
            partNo: {fieldName: "PartNo", type: "string", title: "Part No"},
            vendorCode: {fieldName: "VendorCode", type: "string", title: "Vendor Code"},
            rga: {fieldName: "RGA", type: "string", title: "RGA"},
            reasonCode: {fieldName: "ReasonCode", type: "string", title: "Reason Code"},
            internalNote: {fieldName: "InternalNote", type: "string", title: "Internal Note"},
            qty: {fieldName: "Qty", type: "int", title: "Qty"},
            serialNo: {fieldName: "SerialNo", type: "string", title: "Serial No"},
            customerName: {fieldName: "CustomerName", type: "string", title: "Customer Name"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.warranty_warrantyRatioStats]: {
            warrantyCount: {fieldName: "WarrantyCount", type: "decimal", title: "Warranty Count"},
            returnCount: {fieldName: "ReturnCount", type: "decimal", title: "Return Count"},
            totalSales: {fieldName: "TotalSales", type: "decimal", title: "Total Sales"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_todayPurchases]: {
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_wtdPurchases]: {
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_mtdPurchases]: {
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_ytdPurchases]: {
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_annualPurchases]: {
            year: {fieldName: "Year", type: "int", title: "Year"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_monthlyPurchases]: {
            monthNumber: {fieldName: "MonthNumber", type: "string", title: "Month No"},
            year: {fieldName: "Year", type: "int", title: "Year"},
            month: {fieldName: "Month", type: "string", title: "Month"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases]: {
            year: {fieldName: "Year", type: "int", title: "Year"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_ytdParts]: {
            partNo: {fieldName: "PartNo", type: "string", title: "Part No"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_ytdVendor]: {
            vendorNumber: {fieldName: "Vendor_Number", type: "string", title: "Vendor Number"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_purchaseByCurrency]: {
            currencyDisplay: {fieldName: "currencyDisplay", type: "string", title: "Currency Display"},
            currencyCode: {fieldName: "Currency_Code", type: "string", title: "Currency Code"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_purchaseByTerritory]: {
            territoryCode: {fieldName: "TerritoryCode", type: "string", title: "Territory Code"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_purchaseByWarehouse]: {
            warehouse: {fieldName: "Warehouse", type: "string", title: "Warehouse"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
        [DashboardWidgetIdentifiers.purchase_vendorTreemap]: {
            vendorNumber: {fieldName: "Vendor_Number", type: "string", title: "Vendor Number"},
            cost: {fieldName: "Cost", type: "decimal", title: "Cost"},
            totalQty: {fieldName: "TotalQty", type: "decimal", title: "Total Qty"},
        } satisfies Record<string, WidgetOriginalParameter>,
    };

    private static readonly widgetVariants: Record<string, DashboardWidgetVariantInfo[]> = {
        [DashboardWidgetIdentifiers.sales_todaySales]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.sales_wtdSales]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.sales_mtdSales]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.sales_ytdSales]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.sales_annualSales]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true},
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: false},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false},
        ],
        [DashboardWidgetIdentifiers.sales_monthlySales]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true},
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: false},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false},
        ],
        [DashboardWidgetIdentifiers.sales_ytdAnnualSales]: [
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: true},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false},
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: false},
        ],
        [DashboardWidgetIdentifiers.sales_ytdParts]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdPartsBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdPartsBarChartQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdPartsBarChartAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdPartsBarChartGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_ytdCustomer]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdCustomerBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdCustomerBarChartQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdCustomerBarChartAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesYtdCustomerBarChartGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_salesByCurrency]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_salesByTerritory]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_salesByWarehouse]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_customerTreemap]: [
            {
                type: DashboardWidgetType.treeMap,
                variant: DashboardWidgetVariants.salesCustomerTreemapSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.treeMap,
                variant: DashboardWidgetVariants.salesCustomerTreemapQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.treeMap,
                variant: DashboardWidgetVariants.salesCustomerTreemapAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.treeMap,
                variant: DashboardWidgetVariants.salesCustomerTreemapGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_lostSalesParts]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesPartsBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesPartsBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_lostSalesCustomer]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesCustomerBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesCustomerBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_salesByProvince]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByProvinceBarChartQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByProvinceBarChartAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByProvinceBarChartGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_salesByCity]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByCityBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByCityBarChartQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByCityBarChartAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByCityBarChartGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_salesByAccountManager]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartQuantityBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartAvgMarginBased,
                isDefaultVariant: false
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartGrossProfitBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_lostSalesByProvince]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesByProvinceBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesByProvinceBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.sales_lostSalesByCity]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesByCityBarChartSalesBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.salesLostSalesByCityBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.ar_transactionsByCustomer]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false},
        ],
        [DashboardWidgetIdentifiers.ar_transactionsBalance]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true},
        ],
        [DashboardWidgetIdentifiers.users_customerLogins]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByStatus]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByVendorStatus]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsAnnually]: [
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: false,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByCustomer]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByMonth]: [
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: false,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByWarehouse]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByPart]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByOperator]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByPartCategory]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByReason]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByVendor]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByPartAndSales]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.warranty_warrantyAndReturnPopup]: [
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: true,},
        ],
        [DashboardWidgetIdentifiers.warranty_warrantyRatioStats]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true},
        ],
        [DashboardWidgetIdentifiers.purchase_todayPurchases]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.purchase_wtdPurchases]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.purchase_mtdPurchases]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.purchase_ytdPurchases]: [
            {type: DashboardWidgetType.infoBlock, variant: undefined, isDefaultVariant: true}
        ],
        [DashboardWidgetIdentifiers.purchase_annualPurchases]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true},
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: false},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false},
        ],
        [DashboardWidgetIdentifiers.purchase_monthlyPurchases]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true},
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: false},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false},
        ],
        [DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases]: [
            {type: DashboardWidgetType.lineChart, variant: undefined, isDefaultVariant: true},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false},
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: false},
        ],
        [DashboardWidgetIdentifiers.purchase_ytdParts]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchaseYtdPartsBarChartCostBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchaseYtdPartsBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.purchase_ytdVendor]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchaseYtdVendorBarChartCostBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchaseYtdVendorBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.purchase_purchaseByCurrency]: [
            {type: DashboardWidgetType.barChart, variant: undefined, isDefaultVariant: true,},
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.purchase_purchaseByTerritory]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartCostBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.purchase_purchaseByWarehouse]: [
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartCostBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.barChart,
                variant: DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
        [DashboardWidgetIdentifiers.purchase_vendorTreemap]: [
            {
                type: DashboardWidgetType.treeMap,
                variant: DashboardWidgetVariants.purchaseVendorTreemapCostBased,
                isDefaultVariant: true
            },
            {
                type: DashboardWidgetType.treeMap,
                variant: DashboardWidgetVariants.purchaseVendorTreemapQuantityBased,
                isDefaultVariant: false
            },
            {type: DashboardWidgetType.table, variant: undefined, isDefaultVariant: false,},
        ],
    }

    private static readonly widgetForcedFilters: Partial<Record<string, WidgetForcedFilterRequirement[]>> = {
        [DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales]: [
            {
                filterName: DashboardFilterParameter.PartCategories,
                isRequired: true,
                requiredMinCount: 1,
                requiredMaxCount: 10,
            },
        ],
    }

    private static readonly chartColors: string[] = [
        '#225aa7', '#f26b11', '#298d27', '#c9141a', '#8150ad', '#784237', '#dd58c2', '#6a6d6f', '#b2b515', '#34b0c0',
        '#ff8c00', '#2e8b57', '#8b0000', '#4682b4', '#daa520', '#9932cc', '#ff1493', '#708090', '#00ced1', '#7fff00',
        '#FF5733', '#33FF57', '#3357FF', '#F5A623', '#8E44AD', '#16A085', '#F39C12', '#D35400', '#27AE60', '#2980B9',
        '#C0392B', '#BDC3C7', '#7F8C8D', '#2ECC71', '#E74C3C', '#95A5A6', '#9B59B6', '#2C3E50', '#E67E22', '#1ABC9C',
    ]

    private static readonly chartLabelColors: string[] = [
        '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff',
        '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff',
        '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff',
        '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff',
    ]

    /**
     * Creates an instance of DashboardWidgetConfig.
     * @param _id           The unique identifier for the widget
     * @param _variant       The variant of the widget (if applicable)
     * @param _type          The type of the widget (e.g., chart, table, info block)
     * @param _numberVisibilityMode Compact vs precise number formatting for injected format callbacks
     */
    constructor(
        private readonly _id: string,
        private readonly _variant?: string,
        private readonly _type?: DashboardWidgetType,
        private readonly _numberVisibilityMode: NumberVisibilityMode = DEFAULT_NUMBER_VISIBILITY_MODE,
    ) {
    }

    // -------------------------------------------  Static Getter Properties  -------------------------------------------

    /** The page size for paginated widgets */
    private get pageSize(): number {
        return DashboardWidgetConfig.pageSize;
    }

    /** The sizing configuration for each widget type */
    private get widgetSizingConfig(): Record<DashboardWidgetType | 'default', DashboardWidgetSizing> {
        return DashboardWidgetConfig.widgetSizingConfig;
    }

    /** The widget variants configuration */
    private get widgetVariants() {
        return DashboardWidgetConfig.widgetVariants;
    }

    /** Forced widget-level filter requirements keyed by widget identifier */
    private get widgetForcedFilters() {
        return DashboardWidgetConfig.widgetForcedFilters;
    }

    /** The widget parameters configuration */
    private get widgetParameters() {
        return DashboardWidgetConfig.widgetParameters;
    }

    /** The chart colors */
    private get chartColors(): string[] {
        return DashboardWidgetConfig.chartColors;
    }

    /** The chart label colors */
    private get chartLabelColors(): string[] {
        return DashboardWidgetConfig.chartLabelColors;
    }

    // -------------------------------------------  Instance Getter Properties  -------------------------------------------

    /** The unique identifier for the widget */
    get id(): string {
        return this._id.toLowerCase();
    }

    /** The variant of the widget (if applicable) */
    get variant(): string | undefined {
        return this._variant;
    }

    /** The type of the widget (e.g., chart, table, info block) */
    get type(): DashboardWidgetType | undefined {
        return this._type;
    }

    /** The compatible widget types for the given identifier */
    get compatibleTypes(): DashboardWidgetType[] {
        const types = this.widgetVariants[this.id]?.map(e => e.type) ?? [];
        return Array.from(new Set(types));
    }

    /** Forced widget-level filter requirements that must stay applied for this widget */
    get forcedFilterRequirements(): WidgetForcedFilterRequirement[] {
        return this.widgetForcedFilters[this.id] ?? [];
    }

    /** The sizing information based on the widget type */
    get sizeInformation(): DashboardWidgetSizing {
        return this.widgetSizingConfig[this.type ?? 'default'];
    }

    /** The widget parameters based on the identifier */
    get variants(): DashboardWidgetVariantInfo[] {
        return this.widgetVariants[this.id] ?? [];
    }

    /** The underlying parameters based on the identifier */
    get underlyingParameters(): string[] {
        switch (this.id) {
            case DashboardWidgetIdentifiers.sales_todaySales: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_wtdSales: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_mtdSales: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_ytdSales: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_annualSales: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_monthlySales: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_ytdAnnualSales: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_ytdParts: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.partNo.fieldName, params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartSalesBased) {
                    return [params.sales.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartAvgMarginBased) {
                    return [params.avgMargin.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartGrossProfitBased) {
                    return [params.grossProfit.fieldName];
                } else {
                    return [params.sales.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.sales_ytdCustomer: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.customerName.fieldName, params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartSalesBased) {
                    return [params.sales.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartAvgMarginBased) {
                    return [params.avgMargin.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartGrossProfitBased) {
                    return [params.grossProfit.fieldName];
                } else {
                    return [params.sales.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.sales_salesByCurrency: {
                const params = this.widgetParameters[this.id];
                return [params.sales.fieldName];
            }
            case DashboardWidgetIdentifiers.sales_customerTreemap: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.customerName.fieldName, params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesCustomerTreemapQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesCustomerTreemapSalesBased) {
                    return [params.sales.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesCustomerTreemapAvgMarginBased) {
                    return [params.avgMargin.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesCustomerTreemapGrossProfitBased) {
                    return [params.grossProfit.fieldName];
                } else {
                    return [params.sales.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.sales_salesByTerritory: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartSalesBased) {
                    return [params.sales.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartAvgMarginBased) {
                    return [params.avgMargin.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartGrossProfitBased) {
                    return [params.grossProfit.fieldName];
                } else {
                    return [params.sales.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.sales_salesByWarehouse: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartSalesBased) {
                    return [params.sales.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartAvgMarginBased) {
                    return [params.avgMargin.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartGrossProfitBased) {
                    return [params.grossProfit.fieldName];
                } else {
                    return [params.sales.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.sales_lostSalesParts: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.partNo.fieldName, params.lostSales.fieldName, params.lostQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesPartsBarChartQuantityBased) {
                    return [params.lostQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesPartsBarChartSalesBased) {
                    return [params.lostSales.fieldName];
                } else {
                    return [params.lostSales.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.sales_lostSalesCustomer: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.customerName.fieldName, params.lostSales.fieldName, params.lostQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesCustomerBarChartQuantityBased) {
                    return [params.lostQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesCustomerBarChartSalesBased) {
                    return [params.lostSales.fieldName];
                } else {
                    return [params.lostSales.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.sales_salesByProvince: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else {
                    if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartQuantityBased) {
                        return [params.totalQty.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased) {
                        return [params.sales.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartAvgMarginBased) {
                        return [params.avgMargin.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartGrossProfitBased) {
                        return [params.grossProfit.fieldName];
                    } else {
                        return [params.sales.fieldName];
                    }
                }
            }
            case DashboardWidgetIdentifiers.sales_salesByCity: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else {
                    if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartQuantityBased) {
                        return [params.totalQty.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartSalesBased) {
                        return [params.sales.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartAvgMarginBased) {
                        return [params.avgMargin.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartGrossProfitBased) {
                        return [params.grossProfit.fieldName];
                    } else {
                        return [params.sales.fieldName];
                    }
                }
            }
            case DashboardWidgetIdentifiers.sales_salesByAccountManager: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.sales.fieldName, params.avgMargin.fieldName, params.totalQty.fieldName, params.grossProfit.fieldName];
                } else {
                    if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartQuantityBased) {
                        return [params.totalQty.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased) {
                        return [params.sales.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartAvgMarginBased) {
                        return [params.avgMargin.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartGrossProfitBased) {
                        return [params.grossProfit.fieldName];
                    } else {
                        return [params.sales.fieldName];
                    }
                }
            }
            case DashboardWidgetIdentifiers.sales_lostSalesByProvince: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.lostSales.fieldName, params.lostQty.fieldName];
                } else {
                    if (this.variant === DashboardWidgetVariants.salesLostSalesByProvinceBarChartQuantityBased) {
                        return [params.lostQty.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesLostSalesByProvinceBarChartSalesBased) {
                        return [params.lostSales.fieldName];
                    } else {
                        return [params.lostSales.fieldName];
                    }
                }
            }
            case DashboardWidgetIdentifiers.sales_lostSalesByCity: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.lostSales.fieldName, params.lostQty.fieldName];
                } else {
                    if (this.variant === DashboardWidgetVariants.salesLostSalesByCityBarChartQuantityBased) {
                        return [params.lostQty.fieldName];
                    } else if (this.variant === DashboardWidgetVariants.salesLostSalesByCityBarChartSalesBased) {
                        return [params.lostSales.fieldName];
                    } else {
                        return [params.lostSales.fieldName];
                    }
                }
            }
            case DashboardWidgetIdentifiers.users_customerLogins: {
                const params = this.widgetParameters[this.id];
                return [params.customerName.fieldName, params.loginCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByStatus: {
                const params = this.widgetParameters[this.id];
                return [params.status.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByVendorStatus: {
                const params = this.widgetParameters[this.id];
                return [params.status.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsAnnually: {
                const params = this.widgetParameters[this.id];
                return [params.year.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByCustomer: {
                const params = this.widgetParameters[this.id];
                return [params.customerName.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByMonth: {
                const params = this.widgetParameters[this.id];
                return [params.month.fieldName, params.year.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByWarehouse: {
                const params = this.widgetParameters[this.id];
                return [params.warehouse.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByPart: {
                const params = this.widgetParameters[this.id];
                return [params.partNo.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByOperator: {
                const params = this.widgetParameters[this.id];
                return [params.operatorFirstname.fieldName, params.operatorLastname.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByPartCategory: {
                const params = this.widgetParameters[this.id];
                return [params.category.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByReason: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.reasonCode.fieldName, params.reasonTitle.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
                } else {
                    return [params.reasonCode.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.warranty_requestsByVendor: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.barChart) {
                    return [params.warrantyCount.fieldName, params.returnCount.fieldName];
                }
                return [params.vendorCode.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByPartAndSales: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.barChart) {
                    return [params.totalSales.fieldName, params.totalQty.fieldName, params.warrantyPct.fieldName];
                }
                return [params.partNo.fieldName, params.totalSales.fieldName, params.totalQty.fieldName, params.warrantyPct.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.barChart) {
                    return [params.totalSales.fieldName, params.totalQty.fieldName, params.warrantyPct.fieldName];
                }
                return [params.category.fieldName, params.totalSales.fieldName, params.totalQty.fieldName, params.warrantyPct.fieldName, params.warrantyCount.fieldName, params.returnCount.fieldName];
            }
            case DashboardWidgetIdentifiers.warranty_warrantyAndReturnPopup: {
                const params = this.widgetParameters[this.id];
                return [
                    params.year.fieldName, params.partNo.fieldName, params.vendorCode.fieldName, params.rga.fieldName, params.reasonCode.fieldName, params.internalNote.fieldName,
                    params.qty.fieldName, params.serialNo.fieldName, params.customerName.fieldName,
                ];
            }
            case DashboardWidgetIdentifiers.warranty_warrantyRatioStats: {
                const params = this.widgetParameters[this.id];
                return [
                    params.warrantyCount.fieldName,
                    params.returnCount.fieldName,
                    params.totalSales.fieldName,
                    params.totalQty.fieldName,
                ];
            }
            case DashboardWidgetIdentifiers.ar_transactionsByCustomer: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.customerName.fieldName, params.totalBalance.fieldName, params.totalDebitAmount.fieldName, params.totalCreditAmount.fieldName];
                }
                return [params.customerName.fieldName, params.totalBalance.fieldName, params.totalDebitAmount.fieldName, params.totalCreditAmount.fieldName];
            }
            case DashboardWidgetIdentifiers.ar_transactionsBalance: {
                const params = this.widgetParameters[this.id];
                return [params.totalBalance.fieldName];
            }
            case DashboardWidgetIdentifiers.purchase_todayPurchases:
            case DashboardWidgetIdentifiers.purchase_wtdPurchases:
            case DashboardWidgetIdentifiers.purchase_mtdPurchases:
            case DashboardWidgetIdentifiers.purchase_ytdPurchases:
            case DashboardWidgetIdentifiers.purchase_annualPurchases:
            case DashboardWidgetIdentifiers.purchase_monthlyPurchases:
            case DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases: {
                const params = this.widgetParameters[this.id];
                return [params.cost.fieldName, params.totalQty.fieldName];
            }
            case DashboardWidgetIdentifiers.purchase_ytdParts: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.partNo.fieldName, params.cost.fieldName, params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchaseYtdPartsBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchaseYtdPartsBarChartCostBased) {
                    return [params.cost.fieldName];
                } else {
                    return [params.cost.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.purchase_ytdVendor: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.vendorNumber.fieldName, params.cost.fieldName, params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchaseYtdVendorBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchaseYtdVendorBarChartCostBased) {
                    return [params.cost.fieldName];
                } else {
                    return [params.cost.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.purchase_purchaseByCurrency: {
                const params = this.widgetParameters[this.id];
                return [params.cost.fieldName];
            }
            case DashboardWidgetIdentifiers.purchase_vendorTreemap: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.vendorNumber.fieldName, params.cost.fieldName, params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchaseVendorTreemapQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchaseVendorTreemapCostBased) {
                    return [params.cost.fieldName];
                } else {
                    return [params.cost.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.purchase_purchaseByTerritory: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.cost.fieldName, params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartCostBased) {
                    return [params.cost.fieldName];
                } else {
                    return [params.cost.fieldName];
                }
            }
            case DashboardWidgetIdentifiers.purchase_purchaseByWarehouse: {
                const params = this.widgetParameters[this.id];
                if (this.type === DashboardWidgetType.table) {
                    return [params.cost.fieldName, params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartQuantityBased) {
                    return [params.totalQty.fieldName];
                } else if (this.variant === DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartCostBased) {
                    return [params.cost.fieldName];
                } else {
                    return [params.cost.fieldName];
                }
            }
            default:
                return [];
        }
    }

    /** The page size for paginated widgets */
    get parameters(): WidgetOriginalParameter[] {
        const params = this.widgetParameters[this.id] as Record<string, WidgetOriginalParameter> | undefined;
        return Object.values(params ?? {});
    }

    /** The order by fields based on the identifier */
    get orderByFields(): DashboardWidgetOrderByField[] {
        const params = this.widgetParameters[this.id] as Record<string, WidgetOriginalParameter> | undefined;
        const result: WidgetOriginalParameter[] = Object.values(params ?? {});
        const allFields = result.map<DashboardWidgetOrderByField>(e => ({
            name: e.fieldName,
            title: e.title,
        }));
        switch (this.id) {
            case DashboardWidgetIdentifiers.sales_todaySales:
            case DashboardWidgetIdentifiers.sales_wtdSales:
            case DashboardWidgetIdentifiers.sales_mtdSales:
            case DashboardWidgetIdentifiers.sales_ytdSales:
            case DashboardWidgetIdentifiers.purchase_todayPurchases:
            case DashboardWidgetIdentifiers.purchase_wtdPurchases:
            case DashboardWidgetIdentifiers.purchase_mtdPurchases:
            case DashboardWidgetIdentifiers.purchase_ytdPurchases:
            case DashboardWidgetIdentifiers.ar_transactionsBalance:
            case DashboardWidgetIdentifiers.warranty_warrantyRatioStats: {
                allFields.splice(0, allFields.length);
                break;
            }
            case DashboardWidgetIdentifiers.sales_monthlySales:
            case DashboardWidgetIdentifiers.purchase_monthlyPurchases: {
                const params = this.widgetParameters[this.id];
                const monthField = allFields.find(e => e.name === params.monthNumber.fieldName)!;
                allFields.splice(0, allFields.length, monthField);
                break;
            }
            case DashboardWidgetIdentifiers.sales_annualSales:
            case DashboardWidgetIdentifiers.purchase_annualPurchases: {
                const params = this.widgetParameters[this.id];
                const yearField = allFields.find(e => e.name === params.year.fieldName)!;
                allFields.splice(0, allFields.length, yearField);
                break;
            }
            case DashboardWidgetIdentifiers.sales_ytdAnnualSales:
            case DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases: {
                allFields.splice(0, allFields.length);
                break;
            }
            case DashboardWidgetIdentifiers.sales_ytdCustomer: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.customerId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.sales_customerTreemap:
            case DashboardWidgetIdentifiers.purchase_vendorTreemap: {
                allFields.splice(0, allFields.length);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsAnnually: {
                allFields.splice(0, allFields.length);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByMonth: {
                const params = this.widgetParameters[this.id];
                const monthField = allFields.find(e => e.name === params.monthNumber.fieldName)!;
                allFields.splice(0, allFields.length, monthField);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByReason: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.reasonId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByStatus: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.statusId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByVendorStatus: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.statusId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByCustomer: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.customerId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByPartCategory:
            case DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.categoryId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByOperator: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.operatorId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByVendor: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.vendorId.fieldName), 1);
                break;
            }
            case DashboardWidgetIdentifiers.ar_transactionsByCustomer: {
                const params = this.widgetParameters[this.id];
                allFields.splice(allFields.findIndex(e => e.name === params.customerId.fieldName), 1);
                break;
            }
        }
        return allFields;
    }

    /** The default order by field based on the identifier */
    get defaultOrderByField(): DashboardWidgetOrderByField | undefined {
        let parameter: WidgetOriginalParameter | undefined;
        switch (this.id) {
            default: {
                const params = this.widgetParameters[this.id] as Record<string, WidgetOriginalParameter> | undefined;
                parameter = Object.values(params ?? {})?.[0];
                break;
            }
            case DashboardWidgetIdentifiers.sales_annualSales: {
                const params = this.widgetParameters[this.id];
                parameter = params.year;
                break;
            }
            case DashboardWidgetIdentifiers.sales_monthlySales: {
                const params = this.widgetParameters[this.id];
                parameter = params.monthNumber;
                break;
            }
            case DashboardWidgetIdentifiers.sales_ytdAnnualSales: {
                const params = this.widgetParameters[this.id];
                parameter = params.sales;
                break;
            }
            case DashboardWidgetIdentifiers.sales_ytdParts: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesYtdPartsBarChartGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_ytdCustomer: {
                const params = this.widgetParameters[this.id];
                parameter = params.sales;
                if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesYtdCustomerBarChartGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_salesByCurrency: {
                const params = this.widgetParameters[this.id];
                parameter = params.sales;
                break;
            }
            case DashboardWidgetIdentifiers.sales_salesByTerritory: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByTerritoryBarChartGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_salesByWarehouse: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByWarehouseBarChartGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_customerTreemap: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesCustomerTreemapSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesCustomerTreemapQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesCustomerTreemapAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesCustomerTreemapGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_lostSalesParts: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesLostSalesPartsBarChartSalesBased) {
                    parameter = params.lostSales;
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesPartsBarChartQuantityBased) {
                    parameter = params.lostQty;
                } else {
                    parameter = params.lostSales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_lostSalesCustomer: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesLostSalesCustomerBarChartSalesBased) {
                    parameter = params.lostSales;
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesCustomerBarChartQuantityBased) {
                    parameter = params.lostQty;
                } else {
                    parameter = params.lostSales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_salesByProvince: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByProvinceBarChartGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_salesByCity: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByCityBarChartGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_salesByAccountManager: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased) {
                    parameter = params.sales;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartAvgMarginBased) {
                    parameter = params.avgMargin;
                } else if (this.variant === DashboardWidgetVariants.salesSalesByAccountManagerBarChartGrossProfitBased) {
                    parameter = params.grossProfit;
                } else {
                    parameter = params.sales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_lostSalesByProvince: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesLostSalesByProvinceBarChartSalesBased) {
                    parameter = params.lostSales;
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesByProvinceBarChartQuantityBased) {
                    parameter = params.lostQty;
                } else {
                    parameter = params.lostSales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.sales_lostSalesByCity: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.salesLostSalesByCityBarChartSalesBased) {
                    parameter = params.lostSales;
                } else if (this.variant === DashboardWidgetVariants.salesLostSalesByCityBarChartQuantityBased) {
                    parameter = params.lostQty;
                } else {
                    parameter = params.lostSales;
                }
                break;
            }
            case DashboardWidgetIdentifiers.users_customerLogins: {
                const params = this.widgetParameters[this.id];
                parameter = params.loginCount;
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsAnnually: {
                const params = this.widgetParameters[this.id];
                parameter = params.year;
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByMonth: {
                const params = this.widgetParameters[this.id];
                parameter = params.monthNumber;
                break;
            }
            case DashboardWidgetIdentifiers.warranty_requestsByPartCategory:
            case DashboardWidgetIdentifiers.warranty_requestsByPart:
            case DashboardWidgetIdentifiers.warranty_requestsByOperator:
            case DashboardWidgetIdentifiers.warranty_requestsByCustomer:
            case DashboardWidgetIdentifiers.warranty_requestsByStatus:
            case DashboardWidgetIdentifiers.warranty_requestsByVendorStatus:
            case DashboardWidgetIdentifiers.warranty_requestsByWarehouse:
            case DashboardWidgetIdentifiers.warranty_requestsByReason:
            case DashboardWidgetIdentifiers.warranty_requestsByVendor:
            case DashboardWidgetIdentifiers.warranty_requestsByPartAndSales:
            case DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales: {
                const params = this.widgetParameters[this.id];
                parameter = params.warrantyCount;
                break;
            }
            case DashboardWidgetIdentifiers.warranty_warrantyAndReturnPopup: {
                const params = this.widgetParameters[this.id];
                parameter = params.year;
                break;
            }
            case DashboardWidgetIdentifiers.ar_transactionsByCustomer: {
                const params = this.widgetParameters[this.id];
                parameter = params.totalBalance;
                break;
            }
            case DashboardWidgetIdentifiers.purchase_annualPurchases: {
                const params = this.widgetParameters[this.id];
                parameter = params.year;
                break;
            }
            case DashboardWidgetIdentifiers.purchase_monthlyPurchases: {
                const params = this.widgetParameters[this.id];
                parameter = params.monthNumber;
                break;
            }
            case DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases: {
                const params = this.widgetParameters[this.id];
                parameter = params.cost;
                break;
            }
            case DashboardWidgetIdentifiers.purchase_ytdParts: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.purchaseYtdPartsBarChartCostBased) {
                    parameter = params.cost;
                } else if (this.variant === DashboardWidgetVariants.purchaseYtdPartsBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else {
                    parameter = params.cost;
                }
                break;
            }
            case DashboardWidgetIdentifiers.purchase_ytdVendor: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.purchaseYtdVendorBarChartCostBased) {
                    parameter = params.cost;
                } else if (this.variant === DashboardWidgetVariants.purchaseYtdVendorBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else {
                    parameter = params.cost;
                }
                break;
            }
            case DashboardWidgetIdentifiers.purchase_purchaseByCurrency: {
                const params = this.widgetParameters[this.id];
                parameter = params.cost;
                break;
            }
            case DashboardWidgetIdentifiers.purchase_purchaseByTerritory: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartCostBased) {
                    parameter = params.cost;
                } else if (this.variant === DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else {
                    parameter = params.cost;
                }
                break;
            }
            case DashboardWidgetIdentifiers.purchase_purchaseByWarehouse: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartCostBased) {
                    parameter = params.cost;
                } else if (this.variant === DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartQuantityBased) {
                    parameter = params.totalQty;
                } else {
                    parameter = params.cost;
                }
                break;
            }
            case DashboardWidgetIdentifiers.purchase_vendorTreemap: {
                const params = this.widgetParameters[this.id];
                if (this.variant === DashboardWidgetVariants.purchaseVendorTreemapCostBased) {
                    parameter = params.cost;
                } else if (this.variant === DashboardWidgetVariants.purchaseVendorTreemapQuantityBased) {
                    parameter = params.totalQty;
                } else {
                    parameter = params.cost;
                }
                break;
            }
        }
        return parameter ? {name: parameter.fieldName, title: parameter.title} : undefined;
    }

    /** Indicates if the widget supports pagination */
    get hasPagination(): boolean | undefined {
        switch (this.id) {
            case DashboardWidgetIdentifiers.sales_customerTreemap:
            case DashboardWidgetIdentifiers.sales_monthlySales:
            case DashboardWidgetIdentifiers.warranty_requestsByMonth:
                return false;
            default:
                return this.type !== DashboardWidgetType.infoBlock;
        }
    }

    /** Indicates if the widget supports order by */
    get hasOrderBy(): boolean | undefined {
        if (this.type === DashboardWidgetType.infoBlock) {
            return false;
        }
        return this.orderByFields.length > 0;
    }

    // noinspection JSUnusedLocalSymbols
    /** The minimum height based on the identifier and type */
    getMinHeight(_isLoading?: boolean, _simplified?: boolean, noDataAvailableOrNotImplementedYet?: boolean): GetMinHeightReturnValue {
        const defaultHeight: Record<DashboardWidgetType, GetMinHeightReturnValue> = {
            [DashboardWidgetType.table]: {xs: 406},
            [DashboardWidgetType.lineChart]: {xs: 300,},
            [DashboardWidgetType.barChart]: {xs: 300,},
            [DashboardWidgetType.treeMap]: {xs: 430,},
            [DashboardWidgetType.treeMapOld]: {xs: 430,},
            [DashboardWidgetType.infoBlock]: {xs: 160,},
        }
        if (!this.type || noDataAvailableOrNotImplementedYet) {
            return {xs: 150};
        }
        if (this.type === DashboardWidgetType.infoBlock) {
            const configs = this.getDataConfig();
            const pointsLength = ((configs?.[DashboardWidgetType.infoBlock]?.[0]?.config as InfoBlockConfig)?.points.length || 1);
            return {
                xs: (44 * pointsLength) + (8 * Math.max(pointsLength - 1, 0)) + 32,
            }
        }
        return defaultHeight[this.type];
    }

    /** The container minimum height based on the identifier and type */
    getContainerMinHeight(isLoading?: boolean, _simplified?: boolean, noDataAvailableOrNotImplementedYet?: boolean, hasFetchError?: boolean): GetMinHeightReturnValue {
        const minHeight = this.getMinHeight(isLoading, _simplified, noDataAvailableOrNotImplementedYet) ?? {};

        const paginationHeight = this.hasPagination ? 60 : 0;
        const orderByHeight = this.hasOrderBy ? 60 : 0;
        // simplified still supports orderBy / export / settings; only edit chrome is reduced
        const footerHeight = Math.max(paginationHeight, orderByHeight);

        const headerHeight = 60;
        const paddingOfContent = this.type === DashboardWidgetType.infoBlock ? 16 : 32;
        const gapHeight = ((!!footerHeight ? 1 : 0) + (!!headerHeight ? 1 : 0)) * 8;
        const extraBasedOnType = this.type === DashboardWidgetType.treeMap ? 30 : this.type === DashboardWidgetType.table ? 30 : 0;
        const fetchErrorBannerHeight = hasFetchError ? FETCH_ERROR_BANNER_HEIGHT : 0;

        const additionalHeight = {xs: headerHeight + footerHeight + paddingOfContent + gapHeight + extraBasedOnType + fetchErrorBannerHeight};
        return Object.fromEntries(Object.entries(minHeight as any).map(([key, value]) => {
            return [key, (value as number) + (additionalHeight[key] ?? 0)];
        })) as Property.MinHeight | undefined;
    }

    /** The widget data configuration based on the identifier */
    getDataConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration | undefined {
        switch (this.id) {
            case DashboardWidgetIdentifiers.sales_todaySales:
                return this.getSalesTodaySalesConfig();
            case DashboardWidgetIdentifiers.sales_wtdSales:
                return this.getSalesWtdSalesConfig();
            case DashboardWidgetIdentifiers.sales_mtdSales:
                return this.getSalesMtdSalesConfig();
            case DashboardWidgetIdentifiers.sales_ytdSales:
                return this.getSalesYtdSalesConfig();
            case DashboardWidgetIdentifiers.sales_annualSales:
                return this.getSalesAnnualSalesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_monthlySales:
                return this.getSalesMonthlySalesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_ytdAnnualSales:
                return this.getSalesYtdAnnualSalesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_ytdParts:
                return this.getSalesYtdPartsConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_ytdCustomer:
                return this.getSalesYtdCustomerConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_salesByCurrency:
                return this.getSalesSalesByCurrencyConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_customerTreemap:
                return this.getSalesCustomerTreemapConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_salesByTerritory:
                return this.getSalesSalesByTerritoryConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_salesByWarehouse:
                return this.getSalesSalesByWarehouseConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_lostSalesParts:
                return this.getSalesLostSalesPartsConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_lostSalesCustomer:
                return this.getSalesLostSalesCustomerConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_salesByProvince:
                return this.getSalesSalesByProvinceConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_salesByCity:
                return this.getSalesSalesByCityConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_lostSalesByProvince:
                return this.getSalesLostSalesByProvinceConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_lostSalesByCity:
                return this.getSalesLostSalesByCityConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.sales_salesByAccountManager:
                return this.getSalesSalesByAccountManagerConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.users_customerLogins:
                return this.getUsersCustomerLoginsConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByStatus:
                return this.getWarrantyRequestsByStatusConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByVendorStatus:
                return this.getWarrantyRequestsByVendorStatusConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsAnnually:
                return this.getWarrantyRequestsAnnuallyConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByMonth:
                return this.getWarrantyRequestsByMonthConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByCustomer:
                return this.getWarrantyRequestsByCustomerConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByWarehouse:
                return this.getWarrantyRequestsByWarehouseConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByPart:
                return this.getWarrantyRequestsByPartConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByOperator:
                return this.getWarrantyRequestsByOperatorConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByPartCategory:
                return this.getWarrantyRequestsByPartCategoryConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByReason:
                return this.getWarrantyRequestsByReasonConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByVendor:
                return this.getWarrantyRequestsByVendorConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByPartAndSales:
                return this.getWarrantyRequestsByPartAndSalesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales:
                return this.getWarrantyRequestsByCategoryAndSalesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_warrantyAndReturnPopup:
                return this.getWarrantyWarrantyAndReturnPopupConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.warranty_warrantyRatioStats:
                return this.getWarrantyRatioStatsConfig();
            case DashboardWidgetIdentifiers.ar_transactionsByCustomer:
                return this.getArTransactionsByCustomerConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.ar_transactionsBalance:
                return this.getArTransactionsBalanceConfig();
            case DashboardWidgetIdentifiers.purchase_todayPurchases:
                return this.getPurchaseTodayPurchasesConfig();
            case DashboardWidgetIdentifiers.purchase_wtdPurchases:
                return this.getPurchaseWtdPurchasesConfig();
            case DashboardWidgetIdentifiers.purchase_mtdPurchases:
                return this.getPurchaseMtdPurchasesConfig();
            case DashboardWidgetIdentifiers.purchase_ytdPurchases:
                return this.getPurchaseYtdPurchasesConfig();
            case DashboardWidgetIdentifiers.purchase_annualPurchases:
                return this.getPurchaseAnnualPurchasesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_monthlyPurchases:
                return this.getPurchaseMonthlyPurchasesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases:
                return this.getPurchaseYtdAnnualPurchasesConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_ytdParts:
                return this.getPurchaseYtdPartsConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_ytdVendor:
                return this.getPurchaseYtdVendorConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_purchaseByCurrency:
                return this.getPurchasePurchaseByCurrencyConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_vendorTreemap:
                return this.getPurchaseVendorTreemapConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_purchaseByTerritory:
                return this.getPurchasePurchaseByTerritoryConfig(filters, setFilters);
            case DashboardWidgetIdentifiers.purchase_purchaseByWarehouse:
                return this.getPurchasePurchaseByWarehouseConfig(filters, setFilters);
            default:
                return undefined;
        }
    }

    /** Indicates if the widget supports navigation */
    get hasNavigation() {
        switch (this.id) {
            default:
                return false;
        }
    }

    // noinspection JSUnusedLocalSymbols
    /**Navigates from the dashboard widget to the appropriate page based on the widget type and filters*/
    navigateToAppropriatePage(
        setQuery: ReturnType<typeof useQuery>[1],
        data?: TableDataPoint | BarChartDataPoint | LineChartDataPoint,
    ) {
        switch (this.id) {
            default:
                return;
        }
    }

    // -------------------------------------------------------------- Helpers  --------------------------------------------------------------

    private currencyFormatter = new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        compactDisplay: 'short',
        notation: 'compact'
    });
    private currencyFormatterLong = new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        compactDisplay: 'long',
        notation: 'standard'
    });
    private numberFormatter = new Intl.NumberFormat('en-CA', {
        style: 'decimal',
        compactDisplay: 'short',
        notation: 'compact'
    });
    private numberFormatterStandard = new Intl.NumberFormat('en-CA', {
        style: 'decimal',
        notation: 'standard'
    });
    private percentageFormatter = new Intl.NumberFormat('en-CA', {style: 'percent', maximumFractionDigits: 2});

    /** Format a currency value using the instance number visibility mode. */
    private formatCurrency(value?: string | number, forceCompact = false): string {
        const numberValue = Number(value);
        if (isNaN(numberValue)) {
            return value?.toString() ?? '';
        }
        if (!forceCompact && this._numberVisibilityMode === NumberVisibilityModes.precise) {
            return this.currencyFormatterLong.format(numberValue);
        }
        return this.currencyFormatter.format(numberValue);
    }

    /** Format a percentage value to a more readable format*/
    private formatPercentage(value?: string | number): string {
        return this.percentageFormatter.format(Number(value) / 100);
    }

    /** Format a number value using the instance number visibility mode. */
    private formatNumber(value?: string | number, forceCompact = false): string {
        if (!forceCompact && this._numberVisibilityMode === NumberVisibilityModes.precise) {
            return this.numberFormatterStandard.format(Number(value));
        }
        return this.numberFormatter.format(Number(value));
    }

    /** Format a string value to a more readable format*/
    private formatString(value?: string | number): string {
        return value?.toString() ?? '';
    }

    private chartColorsFetcher = {
        barChartColor: (count: number, currentPage: number) => Array.from({length: count}, (_, i) => this.chartColors[(((currentPage - 1) * this.pageSize) + i) % this.chartColors.length]),
        barChartLabelColor: (context: Context, currentPage: number) => this.chartLabelColors[(((currentPage - 1) * this.pageSize) + context.dataIndex!) % this.chartLabelColors.length],
        treemapChartColor: (context: TreemapScriptableContext) => this.chartColors[context.dataIndex! % this.chartColors.length],
        treemapLabelChartColor: (context: TreemapScriptableContext) => this.chartLabelColors[context.dataIndex! % this.chartLabelColors.length],
    };

    // -----------------------------------------------  Filters  ---------------------------------------------------------------

    /** Filter by Part Number */
    private filterByPartNumber(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, partNumber?: string) {
        if (!partNumber) {
            return;
        }
        setFilters?.(prevState => {
            const existingPartNumbers = prevState.partNumbers?.map(e => JSON.parse(e)) ?? [];
            const index = existingPartNumbers.findIndex(e => e.partNo === partNumber);
            if (index !== -1) {
                existingPartNumbers.splice(index, 1);
            } else {
                existingPartNumbers.push({partNo: partNumber});
            }
            return ({
                ...prevState,
                partNumbers: existingPartNumbers.map(e => JSON.stringify(e))
            });
        });
    }

    /** Filter by Customer */
    private filterByCustomer(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, customerId?: any, customerName?: string) {
        if (!customerId) {
            return;
        }
        setFilters?.(prevState => {
            const existingCustomers = prevState.customers?.map(e => JSON.parse(e)) ?? [];
            const index = existingCustomers.findIndex(e => e.internalId === customerId);
            if (index !== -1) {
                existingCustomers.splice(index, 1);
            } else {
                existingCustomers.push({internalId: customerId, name: customerName ?? ""});
            }
            return ({
                ...prevState,
                customers: existingCustomers.map(e => JSON.stringify(e)),
            });
        })
    }

    /** Filter by Currency */
    private filterByCurrency(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, currencyCode?: string) {
        if (!currencyCode) {
            return;
        }
        setFilters?.(prevState => ({
            ...prevState,
            currency: prevState.currency === currencyCode ? undefined : currencyCode,
        }))
    }

    /** Filter by Territory */
    private filterByTerritory(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, territoryCode?: string) {
        if (!territoryCode) {
            return;
        }
        setFilters?.(prevState => ({
            ...prevState,
            territoryCodes: prevState.territoryCodes?.includes(territoryCode) ? prevState.territoryCodes.filter(e => e !== territoryCode) : ([...(prevState.territoryCodes ?? []), territoryCode])
        }));
    }

    /** Filter by Vendor Code */
    private filterByVendorCode(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, vendorCode?: string) {
        if (!vendorCode) {
            return;
        }
        setFilters?.(prevState => ({
            ...prevState,
            vendorNumbers: prevState.vendorNumbers?.includes(vendorCode) ? prevState.vendorNumbers.filter(e => e !== vendorCode) : ([...(prevState.vendorNumbers ?? []), vendorCode])
        }));
    }

    /** Filter by Part Support Reason */
    private filterByPartSupportReason(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, reasonId?: number, reasonCode?: string, reasonTitle?: string) {
        if (!reasonId) {
            return;
        }
        setFilters?.(prevState => {
            const existingReasons = prevState.partSupportReasons?.map(e => JSON.parse(e)) ?? [];
            const index = existingReasons.findIndex(e => e.id === reasonId);
            if (index !== -1) {
                existingReasons.splice(index, 1);
            } else {
                existingReasons.push({id: reasonId, code: reasonCode ?? "", title: reasonTitle ?? ""});
            }
            return ({
                ...prevState,
                partSupportReasons: existingReasons.map(e => JSON.stringify(e)),
            });
        });
    }

    /** Filter by Part Support Status */
    private filterByPartSupportStatus(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, statusId?: number, statusTitle?: string) {
        if (!statusId) {
            return;
        }
        setFilters?.(prevState => {
            const existingStatuses = prevState.partSupportStatuses?.map(e => JSON.parse(e)) ?? [];
            const index = existingStatuses.findIndex(e => e.id === statusId);
            if (index !== -1) {
                existingStatuses.splice(index, 1);
            } else {
                existingStatuses.push({id: statusId, title: statusTitle ?? ""});
            }
            return ({
                ...prevState,
                partSupportStatuses: existingStatuses.map(e => JSON.stringify(e)),
            });
        });
    }

    /** Filter by Part Support Vendor Status */
    private filterByPartSupportVendorStatus(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, statusId?: number, statusTitle?: string) {
        if (!statusId) {
            return;
        }
        setFilters?.(prevState => {
            const existingStatuses = prevState.partSupportVendorStatuses?.map(e => JSON.parse(e)) ?? [];
            const index = existingStatuses.findIndex(e => e.id === statusId);
            if (index !== -1) {
                existingStatuses.splice(index, 1);
            } else {
                existingStatuses.push({id: statusId, title: statusTitle ?? ""});
            }
            return ({
                ...prevState,
                partSupportVendorStatuses: existingStatuses.map(e => JSON.stringify(e)),
            });
        });
    }

    /** Filter by Part Category */
    private filterByPartCategory(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, categoryId?: number, categoryTitle?: string) {
        if (!categoryId) {
            return;
        }
        setFilters?.(prevState => {
            const existingStatuses = prevState.categories?.map(e => JSON.parse(e)) ?? [];
            const index = existingStatuses.findIndex(e => e.id === categoryId);
            if (index !== -1) {
                existingStatuses.splice(index, 1);
            } else {
                existingStatuses.push({id: categoryId, title: categoryTitle ?? ""});
            }
            return ({
                ...prevState,
                categories: existingStatuses.map(e => JSON.stringify(e)),
            });
        });
    }

    /** Filter by Operator */
    private filterByOperator(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, operatorId?: number, operatorFirstname?: string, operatorLastname?: string) {
        if (!operatorId) {
            return;
        }
        setFilters?.(prevState => {
            const existingOperators = prevState.operators?.map(e => JSON.parse(e)) ?? [];
            const index = existingOperators.findIndex(e => e.id === operatorId);
            if (index !== -1) {
                existingOperators.splice(index, 1);
            } else {
                existingOperators.push({
                    id: operatorId,
                    firstName: operatorFirstname ?? "",
                    lastName: operatorLastname ?? ""
                });
            }
            return ({
                ...prevState,
                operators: existingOperators.map(e => JSON.stringify(e)),
            });
        });
    }

    /** Filter by Warehouse */
    private filterByWarehouse(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, warehouseCode?: string) {
        if (!warehouseCode) {
            return;
        }
        setFilters?.(prevState => {
            const existingWarehouses = prevState.warehouses?.map(e => JSON.parse(e)) ?? [];
            const index = existingWarehouses.findIndex(e => e.code === warehouseCode);
            if (index !== -1) {
                existingWarehouses.splice(index, 1);
            } else {
                existingWarehouses.push({code: warehouseCode});
            }
            return ({
                ...prevState,
                warehouses: existingWarehouses.map(e => JSON.stringify(e)),
            });
        });
    };

    /**Filter by Province Code*/
    private filterByProvinceCode(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, provinceCode?: string) {
        if (!provinceCode) {
            return;
        }
        setFilters?.(prevState => {
            const existingProvinces = prevState.provinces?.map(e => JSON.parse(e)) ?? [];
            const index = existingProvinces.findIndex(e => e.code === provinceCode);
            if (index !== -1) {
                existingProvinces.splice(index, 1);
            } else {
                existingProvinces.push({code: provinceCode});
            }
            return ({
                ...prevState,
                provinces: existingProvinces.map(e => JSON.stringify(e)),
            });
        });
    }

    /**Filter by City*/
    private filterByCity(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, cityName?: string) {
        if (!cityName) {
            return;
        }
        setFilters?.(prevState => {
            const existingCities = prevState.cities ?? [];
            const index = existingCities.findIndex(e => e === cityName);
            if (index !== -1) {
                existingCities.splice(index, 1);
            } else {
                existingCities.push(cityName);
            }
            return ({
                ...prevState,
                cities: existingCities,
            });
        });
    }

    /**Filter by Account Manager*/
    private filterByAccountManager(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, accountManagerCode?: string) {
        if (!accountManagerCode) {
            return;
        }
        setFilters?.(prevState => {
            const existingRecords = prevState.accountManagers?.map(e => JSON.parse(e)) ?? [];
            const index = existingRecords.findIndex(e => e.code === accountManagerCode);
            if (index !== -1) {
                existingRecords.splice(index, 1);
            } else {
                existingRecords.push({code: accountManagerCode});
            }
            return ({
                ...prevState,
                accountManagers: existingRecords.map(e => JSON.stringify(e)),
            });
        });
    }

    /**
     * Filters the data based on the filters provided.
     * @param filters
     * @private
     */
    private getEffectiveDateRangeFromFilters(filters?: DashboardViewFilterSectionFilters): {
        startDate?: DateUtilsTypes.Instance,
        endDate?: DateUtilsTypes.Instance
    } | undefined {
        if (!filters) {
            return undefined;
        }
        const dateRangeValue = DateRangeUtils.getDateRangeValueFromLegacyFilters(filters);
        if (dateRangeValue) {
            const dates = DateRangeUtils.getDateRange(dateRangeValue);
            if (!dates) return undefined;
            return {startDate: dates[0], endDate: dates[1]};
        } else {
            const dateRangeFromFlag = DateRangeUtils.toDateRangeFromDateRangeFlag((filters as any).dateRangeFlag as string | undefined);
            if (dateRangeFromFlag) {
                const dates = DateRangeUtils.getDateRange(dateRangeFromFlag);
                if (!dates) return undefined;
                return {startDate: dates[0], endDate: dates[1]};
            }
            const startDate = (filters as any).startDate ? DateUtils.local((filters as any).startDate) : undefined;
            const endDate = (filters as any).endDate ? DateUtils.local((filters as any).endDate) : undefined;
            return {startDate, endDate};
        }
    }

    /** Checks if the current date range in the filters is within the provided year. If there is no date range in the filters, it returns false. */
    private determineCurrentDateRangeInYear(year: number, filters?: DashboardViewFilterSectionFilters): boolean {
        if (!filters)
            return false;
        const dates = this.getEffectiveDateRangeFromFilters(filters);
        if (!dates)
            return false;
        if (!dates.startDate || !dates.endDate) {
            return false;
        }
        const beginningOfYear = DateUtils.localNow().set('year', year).startOf('year');
        const endOfYear = beginningOfYear.clone().endOf('year').startOf('day');
        return dates.startDate.isSame(beginningOfYear, 'day') && dates.endDate.isSame(endOfYear, 'day');
    }

    /** Filter by Year */
    private filterByYear(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, _year?: number | string) {
        if (!_year) {
            return;
        }
        const year = typeof _year === 'string' ? Number(_year) : _year;
        if (isNaN(year))
            return;
        setFilters?.(prevState => {
            const currentDateRangeInYear = this.determineCurrentDateRangeInYear(year, prevState);
            const beginningOfYear = DateUtils.localNow().set('year', year).startOf('year');
            const endOfYear = beginningOfYear.clone().endOf('year').startOf('day');
            return ({
                ...prevState,
                dateRangeValue: (!currentDateRangeInYear) ? JSON.stringify({
                    mode: 'static',
                    static: {
                        startDate: beginningOfYear.format(DateUtils.dateTimeFormat),
                        endDate: endOfYear.format(DateUtils.dateTimeFormat),
                    }
                }) : undefined,
            });
        });
    }

    /** Checks if the current date range in the filters is within the provided year and month. If there is no date range in the filters, it returns false. */
    private determineCurrentDateRangeInYearAndMonth(year: number, month: number, filters?: DashboardViewFilterSectionFilters): boolean {
        if (!filters)
            return false;
        const dates = this.getEffectiveDateRangeFromFilters(filters);
        if (!dates)
            return false;
        if (!dates.startDate || !dates.endDate) {
            return false;
        }
        const beginningOfMonth = DateUtils.localNow().set('year', year).set('month', month - 1).startOf('month');
        const endOfMonth = beginningOfMonth.clone().endOf('month').startOf('day');
        return dates.startDate.isSame(beginningOfMonth, 'day') && dates.endDate.isSame(endOfMonth, 'day');
    }

    /** Filter by Year and Month */
    private filterByYearAndMonth(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, _year?: number | string, _monthNumber?: number | string) {
        if (!_year || !_monthNumber) {
            return;
        }
        const year = typeof _year === 'string' ? Number(_year) : _year;
        const monthNumber = typeof _monthNumber === 'string' ? Number(_monthNumber) : _monthNumber;
        if (isNaN(year) || isNaN(monthNumber))
            return;

        setFilters?.(prevState => {
            const currentDateRangeInYearAndMonth = this.determineCurrentDateRangeInYearAndMonth(year, monthNumber, prevState);
            const beginningOfMonth = DateUtils.localNow().set('year', year).set('month', monthNumber - 1).startOf('month');
            const endOfMonth = beginningOfMonth.clone().endOf('month').startOf('day');
            return ({
                ...prevState,
                dateRangeValue: (!currentDateRangeInYearAndMonth) ? JSON.stringify({
                    mode: 'static',
                    static: {
                        startDate: beginningOfMonth.format(DateUtils.dateTimeFormat),
                        endDate: endOfMonth.format(DateUtils.dateTimeFormat),
                    }
                }) : undefined,
            });
        });
    }

    /** Determines if the current date range in the filters is from the start of the provided year until now. If there is no date range in the filters, it returns false. */
    private determineCurrentDateRangeInYearToDate(_year: number, filters?: DashboardViewFilterSectionFilters): boolean {
        if (!filters)
            return false;
        const dates = this.getEffectiveDateRangeFromFilters(filters);
        if (!dates)
            return false;
        if (!dates.startDate || !dates.endDate) {
            return false;
        }
        const beginningOfYear = DateUtils.localNow().set('year', _year).startOf('year');
        const currentDate = DateUtils.localNow().startOf('day');
        return dates.startDate.isSame(beginningOfYear, 'day') && dates.endDate.isSame(currentDate, 'day');
    }

    /** Filter by Year to Date */
    private filterByYearToDate(setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>, _year?: number | string) {
        if (!_year) {
            return;
        }
        const year = typeof _year === 'string' ? Number(_year) : _year;
        if (isNaN(year))
            return;
        setFilters?.(prevState => {
            const currentDateRangeInYearToDate = this.determineCurrentDateRangeInYearToDate(year, prevState);
            const beginningOfYear = DateUtils.localNow().set('year', year).startOf('year');
            const yearToDate = DateUtils.localNow().set('year', year).startOf('day');
            return ({
                ...prevState,
                dateRangeValue: (!currentDateRangeInYearToDate) ? JSON.stringify({
                    mode: 'static',
                    static: {
                        startDate: beginningOfYear.format(DateUtils.dateTimeFormat),
                        endDate: yearToDate.format(DateUtils.dateTimeFormat),
                    }
                }) : undefined,
            });
        });
    }

    // -------------------------------------------------------------- CONFIG  --------------------------------------------------------------

    // ------------------------------------- SALES  -------------------------------------

    private getSalesTodaySalesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_todaySales];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value?: string | number) => this.formatPercentage(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getSalesWtdSalesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_wtdSales];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value?: string | number) => this.formatPercentage(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getSalesMtdSalesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_mtdSales];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value?: string | number) => this.formatPercentage(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            }
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getSalesYtdSalesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_ytdSales];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value?: string | number) => this.formatPercentage(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            }
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getSalesAnnualSalesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_annualSales];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.sales.title
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value),
                                forBar: params.avgMargin.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.totalQty.title
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.grossProfit.title
                            }
                        ],
                        yAxes: [
                            {id: params.sales.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                            {id: params.avgMargin.fieldName, position: 'right'},
                            {id: params.grossProfit.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'start', offset: -20,
                                    format: (value) => this.formatCurrency(value, true),
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                yAxisId: params.totalQty.fieldName,
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'start', offset: -20,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                yAxisId: params.avgMargin.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'start', offset: -20,
                                    format: (value) => this.formatPercentage(value),
                                },
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                yAxisId: params.grossProfit.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'start', offset: -20,
                                    format: (value) => this.formatCurrency(value, true),
                                }
                            }
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYear(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        yAxes: [
                            {id: params.sales.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                            {id: params.avgMargin.fieldName, position: 'right'},
                            {id: params.grossProfit.fieldName, position: 'right'}
                        ],
                        lines: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'end', offset: 0,
                                    format: (value) => this.formatCurrency(value, true),
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'start', align: 'start', offset: 0,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                yAxisId: params.avgMargin.fieldName,
                                color: this.chartColors[2],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'start', align: 'start', offset: 0,
                                    format: (value) => this.formatPercentage(value),
                                },
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                yAxisId: params.grossProfit.fieldName,
                                color: this.chartColors[3],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'start', align: 'start', offset: 0,
                                    format: (value) => this.formatCurrency(value, true),
                                },
                            }
                        ],
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.sales.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.totalQty.title
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value),
                                forLine: params.avgMargin.title
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.grossProfit.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYear(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const checked = this.determineCurrentDateRangeInYear(year, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYear(setFilters, year)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.year.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesMonthlySalesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_monthlySales];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKeys: [params.year.fieldName, params.month.fieldName],
                        sortByKeys: [params.year.fieldName, params.monthNumber.fieldName],
                        multiSortBy: (a: any, b: any) => (a[params.year.fieldName] - b[params.year.fieldName]) || (a[params.monthNumber.fieldName] - b[params.monthNumber.fieldName]),
                        xAxisFormatter: (year, month) => [year?.toString(), month?.toString()].filter(Boolean).join(" "),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.sales.title
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value),
                                forBar: params.avgMargin.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.totalQty.title
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.grossProfit.title
                            },
                        ],
                        yAxes: [
                            {id: params.sales.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                            {id: params.avgMargin.fieldName, position: 'right'},
                            {id: params.grossProfit.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.totalQty.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                yAxisId: params.avgMargin.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => darken(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: 20,
                                },
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                yAxisId: params.grossProfit.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: 20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearAndMonth(setFilters, point[params.year.fieldName], point[params.monthNumber.fieldName]),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKeys: [params.year.fieldName, params.month.fieldName],
                        sortByKeys: [params.year.fieldName, params.monthNumber.fieldName],
                        multiSortBy: (a: any, b: any) => (a[params.year.fieldName] - b[params.year.fieldName]) || (a[params.monthNumber.fieldName] - b[params.monthNumber.fieldName]),
                        xAxisFormatter: (year, month) => [year?.toString(), month?.toString()].filter(Boolean).join(" "),
                        yAxes: [
                            {id: params.sales.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                            {id: params.avgMargin.fieldName, position: 'right'},
                            {id: params.grossProfit.fieldName, position: 'right'}
                        ],
                        lines: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'end',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                yAxisId: params.avgMargin.fieldName,
                                color: this.chartColors[2],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                yAxisId: params.grossProfit.fieldName,
                                color: this.chartColors[3],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            }
                        ],
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.sales.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.totalQty.title
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value),
                                forLine: params.avgMargin.title
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.grossProfit.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearAndMonth(setFilters, point[params.year.fieldName], point[params.monthNumber.fieldName]),
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const month = entry[params.monthNumber.fieldName];
                                    const checked = this.determineCurrentDateRangeInYearAndMonth(year, month, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYearAndMonth(setFilters, year, month)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.month.fieldName,
                                title: params.month.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.month.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(`${entry[params.month.fieldName]}-${entry[params.year.fieldName]}`),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesYtdAnnualSalesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_ytdAnnualSales];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        yAxes: [
                            {id: params.sales.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                            {id: params.avgMargin.fieldName, position: 'right'},
                            {id: params.grossProfit.fieldName, position: 'right'}
                        ],
                        lines: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'end',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                yAxisId: params.avgMargin.fieldName,
                                color: this.chartColors[2],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                yAxisId: params.grossProfit.fieldName,
                                color: this.chartColors[3],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            }
                        ],
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.sales.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.totalQty.title
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value),
                                forLine: params.avgMargin.title
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.grossProfit.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearToDate(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: "Selection",
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const checked = this.determineCurrentDateRangeInYearToDate(year, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYearToDate(setFilters, year)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.year.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.sales.title
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value),
                                forBar: params.avgMargin.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.totalQty.title
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.grossProfit.title
                            },
                        ],
                        yAxes: [
                            {id: params.sales.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                            {id: params.avgMargin.fieldName, position: 'right'},
                            {id: params.grossProfit.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.totalQty.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                yAxisId: params.avgMargin.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => darken(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                yAxisId: params.grossProfit.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearToDate(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        return configs;
    }

    private getSalesYtdPartsConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_ytdParts];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),

                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdPartsBarChartSalesBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                yAxisId: params.sales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),

                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdPartsBarChartQuantityBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdPartsBarChartAvgMarginBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                yAxisId: params.avgMargin.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdPartsBarChartGrossProfitBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                yAxisId: params.grossProfit.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const partNumber = entry[params.partNo.fieldName]?.toString();
                                    const checked = filters?.partNumbers?.some(e => JSON.parse(e).partNo === partNumber) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartNumber(setFilters, partNumber)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.partNo.fieldName,
                                title: params.partNo.title,
                                width: 120,
                                format: (value) =>
                                    <ProductInformationPopover partNo={value}>
                                        {value ?? ""}
                                    </ProductInformationPopover>,
                                sortable: sortable.includes(params.partNo.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.partNo.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesYtdCustomerConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_ytdCustomer];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),

                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdCustomerBarChartSalesBased,
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdCustomerBarChartQuantityBased,
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdCustomerBarChartAvgMarginBased,
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesYtdCustomerBarChartGrossProfitBased,
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const customerId = entry[params.customerId.fieldName];
                                    const checked = filters?.customers?.some(e => JSON.parse(e).internalId === customerId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCustomer(setFilters, customerId, entry[params.customerName.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.customerName.fieldName,
                                title: params.customerName.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.customerName.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.customerName.fieldName])}_${this.formatString(entry[params.customerId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesSalesByCurrencyConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_salesByCurrency];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.currencyDisplay.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCurrency(setFilters, point[params.currencyCode.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const checked = filters?.currency === entry[params.currencyCode.fieldName];
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCurrency(setFilters, entry[params.currencyCode.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.currencyDisplay.fieldName,
                                title: params.currencyDisplay.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.currencyDisplay.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.currencyCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesCustomerTreemapConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_customerTreemap];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.treeMap)) {
            const treemapConfig = [
                {
                    config: {
                        labelKey: params.customerName.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => `${value !== null ? this.formatPercentage(value) : "--"}`
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        tree: {
                            key: params.sales.fieldName,
                            label: params.customerName.title,
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.customerName.fieldName] ?? ""}`,
                                        `Sales: ${this.formatCurrency(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                            displayMode: 'headerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            captions: {display: false},
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString()),
                        },
                    } satisfies TreemapWidgetConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesCustomerTreemapSalesBased,
                    config: {
                        labelKey: params.customerName.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => `${value !== null ? this.formatPercentage(value) : "--"}`
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        tree: {
                            key: params.sales.fieldName,
                            label: params.customerName.title,
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.customerName.fieldName] ?? ""}`,
                                        `Sales: ${this.formatCurrency(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                            displayMode: 'headerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            captions: {display: false},
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies TreemapWidgetConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesCustomerTreemapQuantityBased,
                    config: {
                        labelKey: params.customerName.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => `${value !== null ? this.formatPercentage(value) : "--"}`
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        tree: {
                            key: params.totalQty.fieldName,
                            label: params.customerName.title,
                            displayMode: 'containerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.customerName.fieldName] ?? ""}`,
                                        `Qty: ${this.formatNumber(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies TreemapWidgetConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesCustomerTreemapAvgMarginBased,
                    config: {
                        labelKey: params.customerName.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => `${value !== null ? this.formatPercentage(value) : "--"}`
                            },
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        tree: {
                            key: params.avgMargin.fieldName,
                            label: params.customerName.title,
                            displayMode: 'containerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.customerName.fieldName] ?? ""}`,
                                        `Margin: ${this.formatPercentage(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies TreemapWidgetConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesCustomerTreemapGrossProfitBased,
                    config: {
                        labelKey: params.customerName.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => `${value !== null ? this.formatPercentage(value) : "--"}`
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        tree: {
                            key: params.grossProfit.fieldName,
                            label: params.customerName.title,
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.customerName.fieldName] ?? ""}`,
                                        `Gross Profit: ${this.formatCurrency(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                            displayMode: 'headerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            captions: {display: false},
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies TreemapWidgetConfig,
                }
            ] satisfies DashboardWidgetConfiguration['tree-map']
            configs[DashboardWidgetType.treeMap] = treemapConfig;
            configs[DashboardWidgetType.treeMapOld] = treemapConfig;
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const customerIds = filters?.customers?.map(e => JSON.parse(e).internalId) ?? [];
                                    const customerId = entry[params.customerId.fieldName];
                                    const checked = customerIds.includes(customerId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCustomer(setFilters, customerId, entry[params.customerName.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.customerName.fieldName,
                                title: params.customerName.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.customerName.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        bottomRow: {
                            columns: [
                                {
                                    key: params.customerName.fieldName,
                                    value: () => "Total",
                                },
                                {
                                    key: params.sales.fieldName,
                                    value: ({
                                                data,
                                                col
                                            }) => col.format?.(data.reduce((acc, entry) => acc + (entry[params.sales.fieldName] ?? 0), 0)),
                                },
                                {
                                    key: params.avgMargin.fieldName,
                                    value: ({data, col}) => {
                                        const avgMargin = data.map(entry => entry[params.avgMargin.fieldName] ?? 0);
                                        const avgOfAvgMargin = avgMargin.reduce((acc, val) => acc + val, 0) / avgMargin.length;
                                        return col.format?.(avgOfAvgMargin);
                                    },
                                },
                                {
                                    key: params.totalQty.fieldName,
                                    value: ({
                                                data,
                                                col
                                            }) => col.format?.(data.reduce((acc, entry) => acc + (entry[params.totalQty.fieldName] ?? 0), 0)),
                                },
                                {
                                    key: params.grossProfit.fieldName,
                                    value: ({
                                                data,
                                                col
                                            }) => col.format?.(data.reduce((acc, entry) => acc + (entry[params.grossProfit.fieldName] ?? 0), 0)),
                                },
                            ],
                        },
                        getKey: (entry) => `${this.formatString(entry[params.customerName.fieldName])}_${this.formatString(entry[params.customerId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;

    }

    private getSalesSalesByTerritoryConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_salesByTerritory];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartSalesBased,
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartQuantityBased,
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        }
                    }
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartAvgMarginBased,
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByTerritoryBarChartGrossProfitBased,
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const territoryCode = entry[params.territoryCode.fieldName];
                                    const checked = filters?.territoryCodes?.includes(territoryCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByTerritory(setFilters, territoryCode)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.territoryCode.fieldName,
                                title: params.territoryCode.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.territoryCode.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.territoryCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesSalesByWarehouseConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_salesByWarehouse];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartSalesBased,
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartQuantityBased,
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        }
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartAvgMarginBased,
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByWarehouseBarChartGrossProfitBased,
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const warehouseCode = entry[params.warehouse.fieldName];
                                    const checked = filters?.warehouses?.map(e => JSON.parse(e)?.code).includes(warehouseCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByWarehouse(setFilters, warehouseCode)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.warehouse.fieldName,
                                title: params.warehouse.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.warehouse.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.warehouse.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    // ------------------------------------- PURCHASE  -------------------------------------

    private getPurchaseTodayPurchasesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_todayPurchases];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getPurchaseWtdPurchasesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_wtdPurchases];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getPurchaseMtdPurchasesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_mtdPurchases];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getPurchaseYtdPurchasesConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_ytdPurchases];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value?: string | number) => this.formatNumber(value)
                            },
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getPurchaseAnnualPurchasesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_annualPurchases];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.cost.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.totalQty.title
                            },
                        ],
                        yAxes: [
                            {id: params.cost.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'start', offset: -20,
                                    format: (value) => this.formatCurrency(value, true),
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                yAxisId: params.totalQty.fieldName,
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'start', offset: -20,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYear(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        yAxes: [
                            {id: params.cost.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                        ],
                        lines: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'end', offset: 0,
                                    format: (value) => this.formatCurrency(value, true),
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'start', align: 'start', offset: 0,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                        ],
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.cost.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.totalQty.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYear(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const checked = this.determineCurrentDateRangeInYear(year, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYear(setFilters, year)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.year.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getPurchaseMonthlyPurchasesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_monthlyPurchases];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKeys: [params.year.fieldName, params.month.fieldName],
                        sortByKeys: [params.year.fieldName, params.monthNumber.fieldName],
                        multiSortBy: (a: any, b: any) => (a[params.year.fieldName] - b[params.year.fieldName]) || (a[params.monthNumber.fieldName] - b[params.monthNumber.fieldName]),
                        xAxisFormatter: (year, month) => [year?.toString(), month?.toString()].filter(Boolean).join(" "),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.cost.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.totalQty.title
                            },
                        ],
                        yAxes: [
                            {id: params.cost.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.totalQty.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearAndMonth(setFilters, point[params.year.fieldName], point[params.monthNumber.fieldName]),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKeys: [params.year.fieldName, params.month.fieldName],
                        sortByKeys: [params.year.fieldName, params.monthNumber.fieldName],
                        multiSortBy: (a: any, b: any) => (a[params.year.fieldName] - b[params.year.fieldName]) || (a[params.monthNumber.fieldName] - b[params.monthNumber.fieldName]),
                        xAxisFormatter: (year, month) => [year?.toString(), month?.toString()].filter(Boolean).join(" "),
                        yAxes: [
                            {id: params.cost.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                        ],
                        lines: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'end',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                        ],
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.cost.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.totalQty.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearAndMonth(setFilters, point[params.year.fieldName], point[params.monthNumber.fieldName]),
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const month = entry[params.monthNumber.fieldName];
                                    const checked = this.determineCurrentDateRangeInYearAndMonth(year, month, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYearAndMonth(setFilters, year, month)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.month.fieldName,
                                title: params.month.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.month.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(`${entry[params.month.fieldName]}-${entry[params.year.fieldName]}`),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getPurchaseYtdAnnualPurchasesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        yAxes: [
                            {id: params.cost.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                        ],
                        lines: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'end',
                                    offset: 0,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'start',
                                    align: 'start',
                                    offset: 0,
                                },
                            },
                        ],
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value),
                                forLine: params.cost.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.totalQty.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearToDate(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: "Selection",
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const checked = this.determineCurrentDateRangeInYearToDate(year, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYearToDate(setFilters, year)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.year.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.cost.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.totalQty.title
                            },
                        ],
                        yAxes: [
                            {id: params.cost.fieldName, position: 'left'},
                            {id: params.totalQty.fieldName, position: 'left'},
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.totalQty.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearToDate(setFilters, point[params.year.fieldName]),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        return configs;
    }

    private getPurchaseYtdPartsConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_ytdParts];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchaseYtdPartsBarChartCostBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                yAxisId: params.cost.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchaseYtdPartsBarChartQuantityBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const partNumber = entry[params.partNo.fieldName];
                                    const checked = filters?.partNumbers?.some(e => JSON.parse(e).partNo === partNumber) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartNumber(setFilters, partNumber)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.partNo.fieldName,
                                title: params.partNo.title,
                                width: 120,
                                format: (value) =>
                                    <ProductInformationPopover partNo={value}>
                                        {value ?? ""}
                                    </ProductInformationPopover>,
                                sortable: sortable.includes(params.partNo.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.partNo.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getPurchaseYtdVendorConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_ytdVendor];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.vendorNumber.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByVendorCode(setFilters, point[params.vendorNumber.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchaseYtdVendorBarChartCostBased,
                    config: {
                        xAxisKey: params.vendorNumber.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByVendorCode(setFilters, point[params.vendorNumber.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchaseYtdVendorBarChartQuantityBased,
                    config: {
                        xAxisKey: params.vendorNumber.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByVendorCode(setFilters, point[params.vendorNumber.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const vendorNumber = entry[params.vendorNumber.fieldName];
                                    const checked = filters?.vendorNumbers?.includes(vendorNumber) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByVendorCode(setFilters, vendorNumber)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.vendorNumber.fieldName,
                                title: params.vendorNumber.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.vendorNumber.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.vendorNumber.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getPurchasePurchaseByCurrencyConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_purchaseByCurrency];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.currencyDisplay.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCurrency(setFilters, point[params.currencyCode.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const checked = filters?.currency === entry[params.currencyCode.fieldName];
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCurrency(setFilters, entry[params.currencyCode.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.currencyDisplay.fieldName,
                                title: params.currencyDisplay.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.currencyDisplay.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.currencyCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getPurchaseVendorTreemapConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_vendorTreemap];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.treeMap)) {
            const treemapConfig = [
                {
                    config: {
                        labelKey: params.vendorNumber.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        tree: {
                            key: params.cost.fieldName,
                            label: params.vendorNumber.title,
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.vendorNumber.fieldName] ?? ""}`,
                                        `Cost: ${this.formatCurrency(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                            displayMode: 'headerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            captions: {display: false},
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByVendorCode(setFilters, point[params.vendorNumber.fieldName]?.toString()),
                        },
                    } satisfies TreemapWidgetConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchaseVendorTreemapCostBased,
                    config: {
                        labelKey: params.vendorNumber.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        tree: {
                            key: params.cost.fieldName,
                            label: params.vendorNumber.title,
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.vendorNumber.fieldName] ?? ""}`,
                                        `Cost: ${this.formatCurrency(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                            displayMode: 'headerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            captions: {display: false},
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByVendorCode(setFilters, point[params.vendorNumber.fieldName]?.toString()),
                        },
                    } satisfies TreemapWidgetConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchaseVendorTreemapQuantityBased,
                    config: {
                        labelKey: params.vendorNumber.fieldName,
                        labelFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        tree: {
                            key: params.totalQty.fieldName,
                            label: params.vendorNumber.title,
                            displayMode: 'containerBoxes',
                            backgroundColor: (context) => this.chartColorsFetcher.treemapChartColor(context),
                            labels: {
                                display: true,
                                align: 'center',
                                color: (context) => this.chartColorsFetcher.treemapLabelChartColor(context),
                                formatter: (context, _, sum) => {
                                    const data = (context.dataset.data[context.dataIndex] as any)?._data ?? {};
                                    const percentage = (context.raw.v / sum) * 100;
                                    return [
                                        `${data[params.vendorNumber.fieldName] ?? ""}`,
                                        `Qty: ${this.formatNumber(context.raw.v)}`,
                                        this.formatPercentage(percentage),
                                    ];
                                },
                                overflow: 'fit',
                                font: () => [
                                    {
                                        size: 16,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 'normal',
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                    {
                                        size: 14,
                                        family: 'Open Sans',
                                        style: 'normal',
                                        weight: 600,
                                        lineHeight: '1.5em',
                                    } satisfies FontSpec,
                                ]
                            },
                        },
                        dataLabelConfig: {
                            enabled: false,
                        },
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByVendorCode(setFilters, point[params.vendorNumber.fieldName]?.toString()),
                        },
                    } satisfies TreemapWidgetConfig,
                },
            ] satisfies DashboardWidgetConfiguration['tree-map']
            configs[DashboardWidgetType.treeMap] = treemapConfig;
            configs[DashboardWidgetType.treeMapOld] = treemapConfig;
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const vendorNumber = entry[params.vendorNumber.fieldName];
                                    const checked = filters?.vendorNumbers?.includes(vendorNumber) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByVendorCode(setFilters, vendorNumber)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.vendorNumber.fieldName,
                                title: params.vendorNumber.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.vendorNumber.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        bottomRow: {
                            columns: [
                                {
                                    key: params.vendorNumber.fieldName,
                                    value: () => "Total",
                                },
                                {
                                    key: params.cost.fieldName,
                                    value: ({
                                                data,
                                                col
                                            }) => col.format?.(data.reduce((acc, entry) => acc + (entry[params.cost.fieldName] ?? 0), 0)),
                                },
                                {
                                    key: params.totalQty.fieldName,
                                    value: ({
                                                data,
                                                col
                                            }) => col.format?.(data.reduce((acc, entry) => acc + (entry[params.totalQty.fieldName] ?? 0), 0)),
                                },
                            ],
                        },
                        getKey: (entry) => this.formatString(entry[params.vendorNumber.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getPurchasePurchaseByTerritoryConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_purchaseByTerritory];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartCostBased,
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartQuantityBased,
                    config: {
                        xAxisKey: params.territoryCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByTerritory(setFilters, point[params.territoryCode.fieldName]?.toString())
                        }
                    } satisfies BarChartConfig,
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const territoryCode = entry[params.territoryCode.fieldName];
                                    const checked = filters?.territoryCodes?.includes(territoryCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByTerritory(setFilters, territoryCode)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.territoryCode.fieldName,
                                title: params.territoryCode.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.territoryCode.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.territoryCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getPurchasePurchaseByWarehouseConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.purchase_purchaseByWarehouse];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartCostBased,
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.cost.fieldName,
                                label: params.cost.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartQuantityBased,
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.cost.fieldName,
                                label: params.cost.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        }
                    } satisfies BarChartConfig,
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const warehouseCode = entry[params.warehouse.fieldName];
                                    const checked = filters?.warehouses?.map(e => JSON.parse(e)?.code).includes(warehouseCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByWarehouse(setFilters, warehouseCode)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.warehouse.fieldName,
                                title: params.warehouse.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.warehouse.fieldName),
                            },
                            {
                                key: params.cost.fieldName,
                                title: params.cost.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.cost.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.warehouse.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesLostSalesPartsConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_lostSalesParts];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                yAxisId: params.lostSales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesPartsBarChartSalesBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                yAxisId: params.lostSales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),

                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesPartsBarChartQuantityBased,
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                yAxisId: params.lostQty.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const partNo = entry[params.partNo.fieldName];
                                    const checked = filters?.partNumbers?.some(e => JSON.parse(e).partNo === partNo) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartNumber(setFilters, partNo)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.partNo.fieldName,
                                title: params.partNo.title,
                                width: 120,
                                format: (value) =>
                                    <ProductInformationPopover partNo={value}>
                                        {value ?? ""}
                                    </ProductInformationPopover>,
                                sortable: sortable.includes(params.partNo.fieldName),
                            },
                            {
                                key: params.lostSales.fieldName,
                                title: params.lostSales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.lostSales.fieldName),
                            },
                            {
                                key: params.lostQty.fieldName,
                                title: params.lostQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.lostQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.partNo.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesLostSalesCustomerConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_lostSalesCustomer];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),

                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesCustomerBarChartSalesBased,
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesCustomerBarChartQuantityBased,
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const customerId = entry[params.customerId.fieldName];
                                    const checked = filters?.customers?.some(e => JSON.parse(e).internalId === customerId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCustomer(setFilters, customerId, entry[params.customerName.fieldName])}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.customerName.fieldName,
                                title: params.customerName.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.customerName.fieldName),
                            },
                            {
                                key: params.lostSales.fieldName,
                                title: params.lostSales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.lostSales.fieldName),
                            },
                            {
                                key: params.lostQty.fieldName,
                                title: params.lostQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.lostQty.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.customerName.fieldName])}_${this.formatString(entry[params.customerId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getSalesSalesByProvinceConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_salesByProvince];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased,
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByProvinceBarChartQuantityBased,
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByProvinceBarChartAvgMarginBased,
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByProvinceBarChartGrossProfitBased,
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
            ]
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const provinceCode = entry[params.provinceCode.fieldName];
                                    const checked = filters?.provinces?.some(e => JSON.parse(e).provinceCode === provinceCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByProvinceCode(setFilters, provinceCode)}
                                        />
                                    );
                                },
                                sortable: false,
                            },
                            {
                                key: params.provinceCode.fieldName,
                                title: params.provinceCode.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.provinceCode.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.provinceCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                },
            ];
        }
        return configs;
    }

    private getSalesSalesByCityConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_salesByCity];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByCityBarChartSalesBased,
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByCityBarChartQuantityBased,
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByCityBarChartAvgMarginBased,
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByCityBarChartGrossProfitBased,
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
            ]
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const city = entry[params.city.fieldName];
                                    const checked = filters?.cities?.some(e => e === city) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCity(setFilters, city)}
                                        />
                                    );
                                },
                                sortable: false,
                            },
                            {
                                key: params.city.fieldName,
                                title: params.city.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.city.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.city.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                },
            ];
        }
        return configs;
    }

    private getSalesSalesByAccountManagerConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_salesByAccountManager];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.accountManagerCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByAccountManager(setFilters, point[params.accountManagerCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased,
                    config: {
                        xAxisKey: params.accountManagerCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.sales.fieldName,
                                label: params.sales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByAccountManager(setFilters, point[params.accountManagerCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartQuantityBased,
                    config: {
                        xAxisKey: params.accountManagerCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            }
                        ],
                        bars: [
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByAccountManager(setFilters, point[params.accountManagerCode.fieldName]?.toString())
                        },
                    },
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartAvgMarginBased,
                    config: {
                        xAxisKey: params.accountManagerCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByAccountManager(setFilters, point[params.accountManagerCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartGrossProfitBased,
                    config: {
                        xAxisKey: params.accountManagerCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.sales.fieldName,
                                label: params.sales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.avgMargin.fieldName,
                                label: params.avgMargin.title,
                                format: (value) => this.formatPercentage(value)
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                            {
                                key: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                format: (value) => this.formatCurrency(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.grossProfit.fieldName,
                                label: params.grossProfit.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByAccountManager(setFilters, point[params.accountManagerCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
            ];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const code = entry[params.accountManagerCode.fieldName];
                                    const checked = filters?.accountManagers?.some(e => e === code) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByAccountManager(setFilters, code)}
                                        />
                                    );
                                },
                                sortable: false,
                            },
                            {
                                key: params.accountManagerCode.fieldName,
                                title: params.accountManagerCode.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.accountManagerCode.fieldName),
                            },
                            {
                                key: params.sales.fieldName,
                                title: params.sales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.sales.fieldName),
                            },
                            {
                                key: params.avgMargin.fieldName,
                                title: params.avgMargin.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.avgMargin.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.grossProfit.fieldName,
                                title: params.grossProfit.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.grossProfit.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.accountManagerCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ]
        }
        return configs;
    }

    private getSalesLostSalesByProvinceConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_lostSalesByProvince];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesByProvinceBarChartSalesBased,
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesByProvinceBarChartQuantityBased,
                    config: {
                        xAxisKey: params.provinceCode.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByProvinceCode(setFilters, point[params.provinceCode.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const provinceCode = entry[params.provinceCode.fieldName];
                                    const checked = filters?.provinces?.some(e => JSON.parse(e).provinceCode === provinceCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByProvinceCode(setFilters, provinceCode)}
                                        />
                                    );
                                },
                                sortable: false,
                            },
                            {
                                key: params.provinceCode.fieldName,
                                title: params.provinceCode.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.provinceCode.fieldName),
                            },
                            {
                                key: params.lostSales.fieldName,
                                title: params.lostSales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.lostSales.fieldName),
                            },
                            {
                                key: params.lostQty.fieldName,
                                title: params.lostQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.lostQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.provinceCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                },
            ]
        }
        return configs;
    }

    private getSalesLostSalesByCityConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.sales_lostSalesByCity];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesByProvinceBarChartSalesBased,
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
                {
                    variant: DashboardWidgetVariants.salesLostSalesByProvinceBarChartQuantityBased,
                    config: {
                        xAxisKey: params.city.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.lostSales.fieldName,
                                label: params.lostSales.title,
                                format: (value) => this.formatCurrency(value)
                            },
                            {
                                key: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                format: (value) => this.formatNumber(value)
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.lostQty.fieldName,
                                label: params.lostQty.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCity(setFilters, point[params.city.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
            ]
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const city = entry[params.city.fieldName];
                                    const checked = filters?.cities?.some(e => e === city) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCity(setFilters, city)}
                                        />
                                    );
                                },
                                sortable: false,
                            },
                            {
                                key: params.city.fieldName,
                                title: params.city.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.city.fieldName),
                            },
                            {
                                key: params.lostSales.fieldName,
                                title: params.lostSales.title,
                                width: 120,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.lostSales.fieldName),
                            },
                            {
                                key: params.lostQty.fieldName,
                                title: params.lostQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.lostQty.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.city.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ];
        }
        return configs;
    }

    // ------------------------------------- USERS  -------------------------------------

    private getUsersCustomerLoginsConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.users_customerLogins];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const customerId = entry[params.customerId.fieldName];
                                    const checked = filters?.customers?.some(e => JSON.parse(e).internalId === customerId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCustomer(setFilters, customerId, entry[params.customerName.fieldName])}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.customerName.fieldName,
                                title: params.customerName.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.customerName.fieldName),
                            },
                            {
                                key: params.loginCount.fieldName,
                                title: params.loginCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.loginCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.customerName.fieldName])}_${this.formatString(entry[params.customerId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        indexAxis: 'y',
                        interactionMode: 'y',
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.loginCount.fieldName,
                                label: params.loginCount.title,
                                format: (value) => `${value}`
                            },
                        ],
                        bars: [
                            {
                                dataKey: params.loginCount.fieldName,
                                label: params.loginCount.title,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatString(value),
                                    anchor: 'center',
                                    align: 'center',
                                    color: (_, context, currentPage) => this.chartColorsFetcher.barChartLabelColor(context, currentPage),

                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        return configs;
    }

    // ------------------------------------- WARRANTY  -------------------------------------

    private getWarrantyRequestsAnnuallyConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsAnnually];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYear(setFilters, point[params.year.fieldName])
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKey: params.year.fieldName,
                        sortByKey: params.year.fieldName,
                        sortBy: (a: any, b: any) => a - b,
                        xAxisFormatter: (value) => this.formatString(value),
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        lines: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'end', offset: 0,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                yAxisId: params.returnCount.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'start', align: 'start', offset: 0,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                        ],
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.returnCount.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYear(setFilters, point[params.year.fieldName])
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: "Selection",
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const checked = this.determineCurrentDateRangeInYearToDate(year, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYearToDate(setFilters, year)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            }
                        ],
                        getKey: (entry) => this.formatString(entry[params.year.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByMonthConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByMonth];
        const compatibleTypes = this.compatibleTypes;


        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKeys: [params.year.fieldName, params.month.fieldName],
                        sortByKeys: [params.year.fieldName, params.monthNumber.fieldName],
                        multiSortBy: (a: any, b: any) => (a[params.year.fieldName] - b[params.year.fieldName]) || (a[params.monthNumber.fieldName] - b[params.monthNumber.fieldName]),
                        xAxisFormatter: (year, month) => [year?.toString(), month?.toString()].filter(Boolean).join(" "),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearAndMonth(setFilters, point[params.year.fieldName], point[params.monthNumber.fieldName])
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.lineChart)) {
            configs[DashboardWidgetType.lineChart] = [
                {
                    config: {
                        xAxisKeys: [params.year.fieldName, params.month.fieldName],
                        sortByKeys: [params.year.fieldName, params.monthNumber.fieldName],
                        multiSortBy: (a: any, b: any) => {
                            return (a[params.year.fieldName] - b[params.year.fieldName]) || (a[params.monthNumber.fieldName] - b[params.monthNumber.fieldName])
                        },
                        xAxisFormatter: (year, month) => [year?.toString(), month?.toString()].filter(Boolean).join(" "),
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        lines: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: this.chartColors[0],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'end', align: 'end', offset: 0,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                yAxisId: params.returnCount.fieldName,
                                color: this.chartColors[1],
                                dataLabelConfig: {
                                    enabled: true, anchor: 'start', align: 'start', offset: 0,
                                    format: (value) => this.formatNumber(value, true),
                                },
                            },
                        ],
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forLine: params.returnCount.title
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByYearAndMonth(setFilters, point[params.year.fieldName], point[params.monthNumber.fieldName])
                        },
                    } satisfies LineChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.lineChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const year = entry[params.year.fieldName];
                                    const month = entry[params.monthNumber.fieldName];
                                    const checked = this.determineCurrentDateRangeInYearAndMonth(year, month, filters);
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByYearAndMonth(setFilters, year, month)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 80,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.year.fieldName),
                            },
                            {
                                key: params.month.fieldName,
                                title: params.month.title,
                                width: 120,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.month.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            }
                        ],
                        getKey: (entry) => this.formatString(`${entry[params.month.fieldName]}-${entry[params.year.fieldName]}`),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByStatusConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByStatus];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.status.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartSupportStatus(setFilters, parseInt(point[params.statusId.fieldName]?.toString()), point[params.status.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const statusId = entry[params.statusId.fieldName];
                                    const checked = filters?.partSupportStatuses?.some(e => JSON.parse(e).id === statusId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartSupportStatus(setFilters, parseInt(statusId), entry[params.status.fieldName])}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.status.fieldName,
                                title: params.status.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.status.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.statusId.fieldName])}_${this.formatString(entry[params.status.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByVendorStatusConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByVendorStatus];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.status.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartSupportVendorStatus(setFilters, parseInt(point[params.statusId.fieldName]?.toString()), point[params.status.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const statusId = entry[params.statusId.fieldName];
                                    const checked = filters?.partSupportVendorStatuses?.some(e => JSON.parse(e).id === statusId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartSupportVendorStatus(setFilters, parseInt(statusId), entry[params.status.fieldName])}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.status.fieldName,
                                title: params.status.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.status.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.statusId.fieldName])}_${this.formatString(entry[params.status.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByCustomerConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByCustomer];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const customerId = entry[params.customerId.fieldName];
                                    const checked = filters?.customers?.some(e => JSON.parse(e).internalId === customerId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCustomer(setFilters, customerId, entry[params.customerName.fieldName].toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.customerName.fieldName,
                                title: params.customerName.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.customerName.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.customerId.fieldName])}_${this.formatString(entry[params.customerName.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByWarehouseConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByWarehouse];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.warehouse.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByWarehouse(setFilters, point[params.warehouse.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const warehouseCode = entry[params.warehouse.fieldName]?.toString();
                                    const checked = filters?.warehouses?.some(e => JSON.parse(e).code === warehouseCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByWarehouse(setFilters, warehouseCode)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.warehouse.fieldName,
                                title: params.warehouse.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.warehouse.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.warehouse.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByPartConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByPart];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const partNo = entry[params.partNo.fieldName]?.toString();
                                    const checked = filters?.partNumbers?.some(e => JSON.parse(e).partNo === partNo) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartNumber(setFilters, partNo)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.partNo.fieldName,
                                title: params.partNo.title,
                                width: 120,
                                format: (value) =>
                                    <ProductInformationPopover partNo={value}>
                                        {value ?? ""}
                                    </ProductInformationPopover>,
                                sortable: sortable.includes(params.partNo.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.partNo.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByOperatorConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByOperator];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKeys: [params.operatorFirstname.fieldName, params.operatorLastname.fieldName],
                        xAxisFormatter: (firstName, lastName) => this.formatString(firstName) + ' ' + this.formatString(lastName),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByOperator(setFilters, parseInt(point[params.operatorId.fieldName]?.toString()), point[params.operatorFirstname.fieldName]?.toString(), point[params.operatorLastname.fieldName]?.toString())
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const operatorId = parseInt(entry[params.operatorId.fieldName])
                                    const checked = filters?.operators?.some(e => JSON.parse(e).id === operatorId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByOperator(setFilters, operatorId, entry[params.operatorFirstname.fieldName]?.toString(), entry[params.operatorLastname.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.operatorFirstname.fieldName,
                                title: params.operatorFirstname.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.operatorFirstname.fieldName),
                            },
                            {
                                key: params.operatorLastname.fieldName,
                                title: params.operatorLastname.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.operatorLastname.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.operatorId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByPartCategoryConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByPartCategory];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.category.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartCategory(setFilters, parseInt(point[params.categoryId.fieldName]?.toString()), point[params.category.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const categoryId = parseInt(entry[params.categoryId.fieldName]);
                                    const checked = filters?.categories?.some(e => JSON.parse(e).id === categoryId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartCategory(setFilters, categoryId, entry[params.category.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.category.fieldName,
                                title: params.category.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.category.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.categoryId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByReasonConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByReason];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.reasonCode.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartSupportReason(setFilters, parseInt(point[params.reasonId.fieldName]?.toString()), point[params.reasonCode.fieldName]?.toString(), point[params.reasonTitle.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const reasonId = parseInt(entry[params.reasonId.fieldName])
                                    const checked = filters?.partSupportReasons?.some(e => JSON.parse(e).id === reasonId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartSupportReason(setFilters, reasonId, entry[params.reasonCode.fieldName]?.toString(), entry[params.reasonTitle.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.reasonCode.fieldName,
                                title: params.reasonCode.title,
                                width: 110,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.reasonCode.fieldName),
                            },
                            {
                                key: params.reasonTitle.fieldName,
                                title: params.reasonTitle.title,
                                width: 400,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.reasonTitle.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.reasonId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }
        return configs;
    }

    private getWarrantyRequestsByVendorConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByVendor];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.vendorCode.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value),
                                forBar: params.returnCount.title
                            },
                        ],
                        yAxes: [
                            {id: params.warrantyCount.fieldName, position: 'left'},
                            {id: params.returnCount.fieldName, position: 'right'}
                        ],
                        bars: [
                            {
                                dataKey: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                yAxisId: params.warrantyCount.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                yAxisId: params.returnCount.fieldName,
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByVendorCode(setFilters, point[params.vendorCode.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }
        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const vendorCode = entry[params.vendorCode.fieldName];
                                    const checked = filters?.vendorNumbers?.includes(vendorCode) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByVendorCode(setFilters, vendorCode)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.vendorCode.fieldName,
                                title: params.vendorCode.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.vendorCode.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                        ],
                        getKey: (entry) => this.formatString(entry[params.vendorCode.fieldName]),
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }

        return configs;
    }

    private getWarrantyRequestsByPartAndSalesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByPartAndSales];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.partNo.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value ?? '0'),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value ?? '0'),
                                forBar: params.returnCount.title
                            },
                            {
                                key: params.totalSales.fieldName,
                                label: params.totalSales.title,
                                format: (value) => this.formatCurrency(value ?? '0'),
                                forBar: params.totalSales.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value ?? '0'),
                                forBar: params.totalQty.title
                            },
                            {
                                key: params.warrantyPct.fieldName,
                                label: params.warrantyPct.title,
                                format: (value) => this.formatPercentage(value ?? '0'),
                                forBar: params.warrantyPct.title
                            },
                        ],
                        yAxes: [
                            {id: params.totalSales.fieldName, position: 'left'},
                            // TODO: add warrantySalesPct as second left axis
                            {id: params.totalQty.fieldName, position: 'right'},
                            {id: params.warrantyPct.fieldName, position: 'right'},
                        ],
                        bars: [
                            {
                                dataKey: params.totalSales.fieldName,
                                label: params.totalSales.title,
                                yAxisId: params.totalSales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value ?? '0'),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value ?? '0'),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.warrantyPct.fieldName,
                                label: params.warrantyPct.title,
                                yAxisId: params.warrantyPct.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.30)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value ?? '0'),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            }
                        ],
                        filtersConfig: {
                            enabled: false,
                            onClick: (key, point) => this.filterByPartNumber(setFilters, point[params.partNo.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }

        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const partNo = entry[params.partNo.fieldName]?.toString() || '';
                                    const checked = filters?.partNumbers?.some(e => JSON.parse(e).partNo === partNo) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartNumber(setFilters, partNo)}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.partNo.fieldName,
                                title: params.partNo.title,
                                width: 120,
                                format: (value) =>
                                    <ProductInformationPopover partNo={value}>
                                        {value ?? ""}
                                    </ProductInformationPopover>,
                                sortable: sortable.includes(params.partNo.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                            {
                                key: params.totalSales.fieldName,
                                title: params.totalSales.title,
                                width: 100,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.totalSales.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.warrantyPct.fieldName,
                                title: params.warrantyPct.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.warrantyPct.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.partNo.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }

        return configs;
    }

    private getWarrantyRequestsByCategoryAndSalesConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales];
        const compatibleTypes = this.compatibleTypes;
        if (compatibleTypes.includes(DashboardWidgetType.barChart)) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.category.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.warrantyCount.fieldName,
                                label: params.warrantyCount.title,
                                format: (value) => this.formatNumber(value ?? '0'),
                                forBar: params.warrantyCount.title
                            },
                            {
                                key: params.returnCount.fieldName,
                                label: params.returnCount.title,
                                format: (value) => this.formatNumber(value ?? '0'),
                                forBar: params.returnCount.title
                            },
                            {
                                key: params.totalSales.fieldName,
                                label: params.totalSales.title,
                                format: (value) => this.formatCurrency(value ?? '0'),
                                forBar: params.totalSales.title
                            },
                            {
                                key: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                format: (value) => this.formatNumber(value ?? '0'),
                                forBar: params.totalQty.title
                            },
                            {
                                key: params.warrantyPct.fieldName,
                                label: params.warrantyPct.title,
                                format: (value) => this.formatPercentage(value ?? '0'),
                                forBar: params.warrantyPct.title
                            },
                        ],
                        yAxes: [
                            {id: params.totalSales.fieldName, position: 'left'},
                            // TODO: add warrantySalesPct as second left axis
                            {id: params.totalQty.fieldName, position: 'right'},
                            {id: params.warrantyPct.fieldName, position: 'right'},
                        ],
                        bars: [
                            {
                                dataKey: params.totalSales.fieldName,
                                label: params.totalSales.title,
                                yAxisId: params.totalSales.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value ?? '0'),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalQty.fieldName,
                                label: params.totalQty.title,
                                yAxisId: params.totalQty.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatNumber(value ?? '0'),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.warrantyPct.fieldName,
                                label: params.warrantyPct.title,
                                yAxisId: params.warrantyPct.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.30)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatPercentage(value ?? '0'),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            }
                        ],
                        filtersConfig: {
                            enabled: !!setFilters,
                            onClick: (key, point) => this.filterByPartCategory(setFilters, parseInt(point[params.categoryId.fieldName]?.toString()), point[params.category.fieldName]?.toString()),
                        },
                    } satisfies BarChartConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }

        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const categoryId = parseInt(entry[params.categoryId.fieldName]);
                                    const checked = filters?.categories?.some(e => JSON.parse(e).id === categoryId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByPartCategory(setFilters, categoryId, entry[params.category.fieldName]?.toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },

                            {
                                key: params.category.fieldName,
                                title: params.category.title,
                                width: 200,
                                format: (value) => this.formatString(value),
                                sortable: sortable.includes(params.category.fieldName),
                            },
                            {
                                key: params.warrantyCount.fieldName,
                                title: params.warrantyCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.warrantyCount.fieldName),
                            },
                            {
                                key: params.returnCount.fieldName,
                                title: params.returnCount.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.returnCount.fieldName),
                            },
                            {
                                key: params.totalSales.fieldName,
                                title: params.totalSales.title,
                                width: 100,
                                format: (value) => this.formatCurrency(value),
                                sortable: sortable.includes(params.totalSales.fieldName),
                            },
                            {
                                key: params.totalQty.fieldName,
                                title: params.totalQty.title,
                                width: 120,
                                format: (value) => this.formatNumber(value),
                                sortable: sortable.includes(params.totalQty.fieldName),
                            },
                            {
                                key: params.warrantyPct.fieldName,
                                title: params.warrantyPct.title,
                                width: 120,
                                format: (value) => this.formatPercentage(value),
                                sortable: sortable.includes(params.warrantyPct.fieldName),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.categoryId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }

        return configs;
    }

    private getWarrantyWarrantyAndReturnPopupConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_warrantyAndReturnPopup];
        const compatibleTypes = this.compatibleTypes;

        if (compatibleTypes.includes(DashboardWidgetType.table)) {
            const sortable = this.orderByFields.map(e => e.name);
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        density: DataGridDensities.compact,
                        columns: [
                            {
                                key: params.partNo.fieldName,
                                title: params.partNo.title,
                                width: 120,
                                sortable: sortable.includes(params.partNo.fieldName),
                                format: (value) => {
                                    const partNo = value?.toString() || '';
                                    const checked = filters?.partNumbers?.some(e => JSON.parse(e).partNo === partNo) ?? false;
                                    return (
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                            {
                                                !!setFilters &&
                                                <Checkbox
                                                    icon={<CheckboxIcon/>}
                                                    checkedIcon={<ActiveCheckboxIcon/>}
                                                    size={'small'}
                                                    checked={checked}
                                                    sx={{p: 0}}
                                                    onChange={() => this.filterByPartNumber(setFilters, partNo)}
                                                />
                                            }
                                            <ProductInformationPopover partNo={value}>
                                                {value ?? ""}
                                            </ProductInformationPopover>
                                        </Box>
                                    )
                                },
                            },
                            {
                                key: params.year.fieldName,
                                title: params.year.title,
                                width: 100,
                                sortable: sortable.includes(params.year.fieldName),
                                format: (value) => this.formatString(value),
                            },
                            {
                                key: params.vendorCode.fieldName,
                                title: params.vendorCode.title,
                                width: 150,
                                sortable: sortable.includes(params.vendorCode.fieldName),
                                format: (value) => this.formatString(value),
                            },
                            {
                                key: params.rga.fieldName,
                                title: params.rga.title,
                                width: 150,
                                sortable: sortable.includes(params.rga.fieldName),
                                format: (value) => this.formatString(value),
                            },
                            {
                                key: params.reasonCode.fieldName,
                                title: params.reasonCode.title,
                                width: 150,
                                sortable: sortable.includes(params.reasonCode.fieldName),
                                format: (value) => this.formatString(value),
                            },
                            {
                                key: params.internalNote.fieldName,
                                title: params.internalNote.title,
                                width: 200,
                                sortable: sortable.includes(params.internalNote.fieldName),
                                format: (value) => this.formatString(value),
                            },
                            {
                                key: params.qty.fieldName,
                                title: params.qty.title,
                                width: 100,
                                sortable: sortable.includes(params.qty.fieldName),
                                format: (value) => this.formatNumber(value),
                            },
                            {
                                key: params.serialNo.fieldName,
                                title: params.serialNo.title,
                                width: 150,
                                sortable: sortable.includes(params.serialNo.fieldName),
                                format: (value) => this.formatString(value),
                            },
                            {
                                key: params.customerName.fieldName,
                                title: params.customerName.title,
                                width: 240,
                                sortable: sortable.includes(params.customerName.fieldName),
                                format: (value) => this.formatString(value),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.rga.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }


        return configs;
    }

    private getWarrantyRatioStatsConfig(): DashboardWidgetConfiguration {
        const configs: DashboardWidgetConfiguration = {};
        const params = this.widgetParameters[DashboardWidgetIdentifiers.warranty_warrantyRatioStats];
        const compatibleTypes = this.compatibleTypes;

        const formatRatio = (value?: string | number) => {
            if (value === undefined || value === null || value === '') {
                return '--';
            }
            const numericValue = Number(value);
            if (isNaN(numericValue)) {
                return '--';
            }
            return this.formatPercentage(numericValue);
        };

        const toRatioPercent = (numerator?: string | number, denominator?: string | number) => {
            const numericNumerator = Number(numerator);
            const numericDenominator = Number(denominator);
            if (!numericDenominator || isNaN(numericNumerator) || isNaN(numericDenominator)) {
                return undefined;
            }
            return (numericNumerator / numericDenominator) * 100;
        };

        const asCount = (value?: string | number) => {
            const numericValue = Number(value);
            return isNaN(numericValue) ? 0 : numericValue;
        };

        const tooltipRow = (label: string, value: React.ReactNode, nested?: boolean) => (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 3,
                    pl: nested ? 1.5 : 0,
                }}
            >
                <Typography variant="caption" sx={{opacity: nested ? 0.7 : 0.85, lineHeight: 1.4}}>
                    {label}
                </Typography>
                <Typography
                    variant={nested ? 'caption' : 'body2'}
                    sx={{
                        fontWeight: nested ? 500 : 700,
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1.4,
                        whiteSpace: 'nowrap'
                    }}
                >
                    {value}
                </Typography>
            </Box>
        );

        const tooltipPanel = (title: string, children: React.ReactNode) => (
            <Box sx={{minWidth: 220, py: 0.25}}>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                        opacity: 0.7,
                        mb: 1,
                    }}
                >
                    {title}
                </Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                    {children}
                </Box>
            </Box>
        );

        if (compatibleTypes.includes(DashboardWidgetType.infoBlock)) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: '_qtyRatio',
                                label: 'Quantity Ratio',
                                compute: (row) => {
                                    const requestsQty = asCount(row[params.warrantyCount.fieldName]) + asCount(row[params.returnCount.fieldName]);
                                    return toRatioPercent(requestsQty, row[params.totalQty.fieldName]);
                                },
                                format: formatRatio,
                                tooltip: (row) => {
                                    const warrantyCount = asCount(row[params.warrantyCount.fieldName]);
                                    const returnCount = asCount(row[params.returnCount.fieldName]);
                                    const requestsQty = warrantyCount + returnCount;
                                    return tooltipPanel('Quantity breakdown', (
                                        <>
                                            {tooltipRow('Requests Qty', this.formatNumber(requestsQty))}
                                            <Box sx={{borderLeft: '1px solid currentColor', ml: 0.5, py: 0.25}}>
                                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                                                    {tooltipRow('Warranty', this.formatNumber(warrantyCount), true)}
                                                    {tooltipRow('Return', this.formatNumber(returnCount), true)}
                                                </Box>
                                            </Box>
                                            <Divider sx={{borderColor: 'currentColor', opacity: 0.2, my: 0.25}}/>
                                            {tooltipRow('Sales Qty', this.formatNumber(row[params.totalQty.fieldName]))}
                                        </>
                                    ));
                                },
                            },
                            {
                                dataKey: '_amountRatio',
                                label: 'Value/Amount Ratio',
                                format: () => 'Coming soon',
                                tooltip: (row) => tooltipPanel('Value breakdown', (
                                    <>
                                        {tooltipRow('Total Sales Value', this.formatCurrency(row[params.totalSales.fieldName]))}
                                        {tooltipRow(
                                            'Total Requests Value',
                                            <Typography component="span" variant="caption"
                                                        sx={{fontStyle: 'italic', opacity: 0.7}}>
                                                Coming soon
                                            </Typography>,
                                        )}
                                    </>
                                )),
                            },
                        ],
                    },
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }
        return configs;
    }

    private getArTransactionsByCustomerConfig(filters?: DashboardViewFilterSectionFilters, setFilters?: React.Dispatch<React.SetStateAction<DashboardViewFilterSectionFilters>>): DashboardWidgetConfiguration | undefined {
        const _type = this.type;
        // noinspection JSUnusedLocalSymbols
        const _variant = this.variant;
        if (!_type) return;

        const params = this.widgetParameters[DashboardWidgetIdentifiers.ar_transactionsByCustomer] as typeof DashboardWidgetConfig.widgetParameters[typeof DashboardWidgetIdentifiers.ar_transactionsByCustomer];
        const configs: DashboardWidgetConfiguration = {};
        const sortable = this.orderByFields.map(e => e.name);

        if (_type === DashboardWidgetType.table) {
            configs[DashboardWidgetType.table] = [
                {
                    config: {
                        visibleRows: 6,
                        density: DataGridDensities.compact,
                        columns: [
                            {
                                key: "selection",
                                title: '',
                                width: 0,
                                hidden: !setFilters,
                                format: (_, entry) => {
                                    if (!entry || !setFilters) return ' ';
                                    const customerId = entry[params.customerId.fieldName];
                                    const checked = filters?.customers?.some(e => JSON.parse(e).internalId === customerId) ?? false;
                                    return (
                                        <Checkbox
                                            sx={{p: 0}}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            size={'small'}
                                            checked={checked}
                                            onChange={() => this.filterByCustomer(setFilters, customerId, entry[params.customerName.fieldName].toString())}
                                        />
                                    )
                                },
                                sortable: false,
                            },
                            {
                                key: params.customerName.fieldName,
                                title: params.customerName.title,
                                width: 240,
                                sortable: sortable.includes(params.customerName.fieldName),
                                format: (value) => this.formatString(value),
                            },
                            {
                                key: params.totalBalance.fieldName,
                                title: params.totalBalance.title,
                                width: 140,
                                sortable: sortable.includes(params.totalBalance.fieldName),
                                format: (value) => this.formatCurrency(value),
                            },
                            {
                                key: params.totalDebitAmount.fieldName,
                                title: params.totalDebitAmount.title,
                                width: 140,
                                sortable: sortable.includes(params.totalDebitAmount.fieldName),
                                format: (value) => this.formatCurrency(value),
                            },
                            {
                                key: params.totalCreditAmount.fieldName,
                                title: params.totalCreditAmount.title,
                                width: 140,
                                sortable: sortable.includes(params.totalCreditAmount.fieldName),
                                format: (value) => this.formatCurrency(value),
                            },
                        ],
                        getKey: (entry) => `${this.formatString(entry[params.customerId.fieldName])}`,
                        pagination: {enabled: true, pageSize: this.pageSize},
                    } satisfies TableConfig,
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.table];
        }

        if (_type === DashboardWidgetType.barChart) {
            configs[DashboardWidgetType.barChart] = [
                {
                    config: {
                        xAxisKey: params.customerName.fieldName,
                        xAxisFormatter: (value) => this.formatString(value),
                        tooltipConfig: [
                            {
                                key: params.totalBalance.fieldName,
                                label: params.totalBalance.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.totalBalance.title,
                            },
                            {
                                key: params.totalDebitAmount.fieldName,
                                label: params.totalDebitAmount.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.totalDebitAmount.title,
                            },
                            {
                                key: params.totalCreditAmount.fieldName,
                                label: params.totalCreditAmount.title,
                                format: (value) => this.formatCurrency(value),
                                forBar: params.totalCreditAmount.title,
                            },
                        ],
                        yAxes: [
                            {id: params.totalBalance.fieldName, position: 'left'},
                            {id: params.totalDebitAmount.fieldName, position: 'right'},
                            {id: params.totalCreditAmount.fieldName, position: 'right'},
                        ],
                        bars: [
                            {
                                dataKey: params.totalBalance.fieldName,
                                label: params.totalBalance.title,
                                yAxisId: params.totalBalance.fieldName,
                                color: (count, currentPage) => this.chartColorsFetcher.barChartColor(count, currentPage),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalDebitAmount.fieldName,
                                label: params.totalDebitAmount.title,
                                yAxisId: params.totalDebitAmount.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.15)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                            {
                                dataKey: params.totalCreditAmount.fieldName,
                                label: params.totalCreditAmount.title,
                                yAxisId: params.totalCreditAmount.fieldName,
                                color: (count, currentPage) => Array.from({length: count}, (_, i) => lighten(this.chartColorsFetcher.barChartColor(count, currentPage)[i], 0.30)),
                                dataLabelConfig: {
                                    enabled: true,
                                    format: (value) => this.formatCurrency(value, true),
                                    anchor: 'end',
                                    align: 'start',
                                    offset: -20,
                                },
                            },
                        ],
                        filtersConfig: {
                            enabled: true,
                            onClick: (key, point) => this.filterByCustomer(setFilters, point[params.customerId.fieldName], point[params.customerName.fieldName].toString()),
                        },
                    } satisfies BarChartConfig
                }
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.barChart];
        }

        return configs;
    }

    private getArTransactionsBalanceConfig(): DashboardWidgetConfiguration | undefined {
        const _type = this.type;
        if (!_type) return;

        const params = this.widgetParameters[DashboardWidgetIdentifiers.ar_transactionsBalance] as typeof DashboardWidgetConfig.widgetParameters[typeof DashboardWidgetIdentifiers.ar_transactionsBalance];
        const configs: DashboardWidgetConfiguration = {};

        if (_type === DashboardWidgetType.infoBlock) {
            configs[DashboardWidgetType.infoBlock] = [
                {
                    config: {
                        points: [
                            {
                                dataKey: params.totalBalance.fieldName,
                                label: params.totalBalance.title,
                                format: (value?: string | number) => this.formatCurrency(value)
                            }
                        ],
                    }
                },
            ] satisfies DashboardWidgetConfiguration[DashboardWidgetType.infoBlock];
        }

        return configs;
    }

}